import "./App.css";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Sidebar from "./components/Sidebar";
import headerLogo from "./assets/logos/logo-header-tight-196x96.png";
import footerLogo from "./assets/logos/logo-footer-120x80.png";

const HOME_POST_GROUPS = [
  { key: "bestSeller", title: "Phụ tùng bán chạy" },
  { key: "newParts", title: "Phụ tùng mới" },
  { key: "supplies", title: "Phụ kiện và vật tư tiêu hao" },
];

const HOME_SKELETON_ITEMS = Array.from({ length: 8 }, (_, index) => index);
const HOME_GROUPS_CLIENT_CACHE_TTL_MS = 300 * 1000;
const homeGroupsClientCache = new Map();
const ADMIN_POSTS_LIMIT = 50;
const MECHANIC_MEDIA_PAGE_LIMIT = 30;
const MAX_MECHANIC_UPLOAD_FILES = 8;
const ORDER_CART_STORAGE_KEY = "my-website-order-cart";
const SITE_VISIT_TOKEN_STORAGE_KEY = "my-website-site-visit-token";
const CONTACT_HOTLINE = "0388861168";
const CONTACT_EMAIL = "info@phutungoto.shop";
const CONTACT_ADDRESS = "TDP Phượng Nghĩa, Phường Chương Mỹ, Hà Nội, Việt Nam";
const CONTACT_CHANNELS = [
  {
    key: "phone",
    label: "Gọi ngay",
    detail: "0388 861 168",
    href: `tel:${CONTACT_HOTLINE}`,
    external: false,
    icon: "phone",
  },
  {
    key: "zalo",
    label: "Chat Zalo",
    detail: "Tư vấn nhanh",
    href: `https://zalo.me/${CONTACT_HOTLINE}`,
    external: true,
    icon: "chat",
  },
  {
    key: "email",
    label: "Gửi email",
    detail: CONTACT_EMAIL,
    href: `mailto:${CONTACT_EMAIL}`,
    external: false,
    icon: "mail",
  },
];

const normalizeSearchText = (input) =>
  String(input || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .toLowerCase()
    .trim();

const isTooShortAlphabeticSearchQuery = (query) => {
  const normalized = normalizeSearchText(query);

  if (!normalized) {
    return false;
  }

  const hasDigit = /\d/.test(normalized);
  const letterOnlyLength = normalized.replace(/[^a-z]/g, "").length;

  return !hasDigit && letterOnlyLength > 0 && letterOnlyLength < 3;
};

function ResponsiveSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const handleSidebarPartSelect = useCallback(
    (selection) => {
      const normalizedSelection =
        selection && typeof selection === "object" ? selection : null;
      const carName = String(normalizedSelection?.carName || "").trim();
      const category = String(normalizedSelection?.category || "").trim();

      if (!carName || !category) {
        return;
      }

      const params = new URLSearchParams({ carName, category });
      navigate(`/search?${params.toString()}`);
    },
    [navigate],
  );

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isMobileSidebarOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileSidebarOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileSidebarOpen]);

  return (
    <>
      <div className="mobile-sidebar-toggle-bar">
        <button
          type="button"
          className="mobile-sidebar-toggle"
          onClick={() => setIsMobileSidebarOpen(true)}
          aria-expanded={isMobileSidebarOpen}
          aria-controls="mobile-sidebar-drawer"
        >
          <span className="mobile-sidebar-toggle-label">PHỤ TÙNG CHO XE</span>
          <span className="mobile-sidebar-toggle-icon" aria-hidden="true">☰</span>
        </button>
      </div>

      <aside className="home-market-left">
        <button
          type="button"
          className={`mobile-sidebar-backdrop ${isMobileSidebarOpen ? "open" : ""}`}
          aria-label="Đóng danh mục phụ tùng"
          onClick={() => setIsMobileSidebarOpen(false)}
        />

        <div
          id="mobile-sidebar-drawer"
          className={`mobile-sidebar-drawer ${isMobileSidebarOpen ? "open" : ""}`}
        >
          <div className="mobile-sidebar-drawer-header">
            <div>
              <p className="mobile-sidebar-drawer-kicker">Danh mục</p>
              <h2>Phụ tùng cho xe</h2>
            </div>
            <button
              type="button"
              className="mobile-sidebar-close"
              onClick={() => setIsMobileSidebarOpen(false)}
              aria-label="Đóng danh mục phụ tùng"
            >
              ×
            </button>
          </div>
          <Sidebar onPartSelect={handleSidebarPartSelect} />
        </div>
      </aside>
    </>
  );
}

const getHomeGroupsCacheKey = (pageSize, groupPages) =>
  JSON.stringify({ pageSize, groupPages });

const getCachedHomeGroupsPayload = (cacheKey) => {
  const cachedEntry = homeGroupsClientCache.get(cacheKey);
  if (!cachedEntry) {
    return null;
  }

  if (cachedEntry.expiresAt <= Date.now()) {
    homeGroupsClientCache.delete(cacheKey);
    return null;
  }

  return cachedEntry.payload;
};

const setCachedHomeGroupsPayload = (cacheKey, payload) => {
  const now = Date.now();

  for (const [key, value] of homeGroupsClientCache.entries()) {
    if (value.expiresAt <= now) {
      homeGroupsClientCache.delete(key);
    }
  }

  homeGroupsClientCache.set(cacheKey, {
    payload,
    expiresAt: now + HOME_GROUPS_CLIENT_CACHE_TTL_MS,
  });
};

const POST_SHARED_IMAGES = [
  {
    src: "/images/Phu_tung_o_to_Hyundai_chinh_hang.webp",
    alt: "Phụ tùng ô tô Hyundai chính hãng",
  },
];

const readJsonSafe = async (response) => {
  const raw = await response.text();

  if (!raw) {
    return {};
  }

  try {
    return JSON.parse(raw);
  } catch {
    return { rawMessage: raw };
  }
};

const getApiErrorMessage = (payload, fallback) => {
  if (payload?.error) {
    return payload.error;
  }

  if (payload?.detail) {
    return payload.detail;
  }

  if (payload?.rawMessage) {
    return payload.rawMessage;
  }

  return fallback;
};

const stripStyleAttributes = (html) => {
  if (!html || typeof html !== "string") {
    return html;
  }

  const isAllowedStyleValue = (value) => {
    const normalizedValue = String(value || "").trim().toLowerCase();
    return (
      /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(normalizedValue) ||
      /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}(\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(normalizedValue)
    );
  };

  const sanitizedStyles = html.replace(/\s+style="([^"]*)"/gi, (_, styleContent) => {
    const safeDeclarations = String(styleContent || "")
      .split(";")
      .map((declaration) => declaration.trim())
      .filter(Boolean)
      .map((declaration) => {
        const separatorIndex = declaration.indexOf(":");
        if (separatorIndex === -1) {
          return null;
        }

        const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
        const value = declaration.slice(separatorIndex + 1).trim();

        if ((property === "color" || property === "background-color") && isAllowedStyleValue(value)) {
          return `${property}: ${value}`;
        }

        return null;
      })
      .filter(Boolean);

    return safeDeclarations.length > 0 ? ` style="${safeDeclarations.join("; ")}"` : "";
  });

  // Remove placeholder paragraphs that only contain whitespace, nbsp, or <br>.
  return sanitizedStyles
    .replace(/<p>\s*(?:&nbsp;|&#160;|<br\s*\/?\s*>|\s)*\s*<\/p>/gi, "")
    .replace(/(?:\s*<br\s*\/?\s*>\s*){3,}/gi, "<br><br>");
};

const decodeBasicEntities = (value) =>
  String(value || "")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");

const extractSeoPostH1ForHeader = (html) => {
  if (!html || typeof html !== "string") {
    return {
      headingText: "",
      bodyWithoutHeading: html,
    };
  }

  const articleMatch = html.match(
    /<article\b[^>]*class=(['"])\s*[^'"]*\bseo-post\b[^'"]*\1[^>]*>[\s\S]*?<\/article>/i,
  );

  if (!articleMatch) {
    return {
      headingText: "",
      bodyWithoutHeading: html,
    };
  }

  const articleHtml = articleMatch[0];
  const h1Match = articleHtml.match(/<h1\b[^>]*>([\s\S]*?)<\/h1>/i);

  if (!h1Match) {
    return {
      headingText: "",
      bodyWithoutHeading: html,
    };
  }

  const headingText = decodeBasicEntities(h1Match[1])
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const articleWithoutHeading = articleHtml.replace(h1Match[0], "").replace(/\n{3,}/g, "\n\n");

  return {
    headingText,
    bodyWithoutHeading: html.replace(articleHtml, articleWithoutHeading),
  };
};

const slugifyText = (input) =>
  String(input || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const buildPostPathFromPartNo = (partNo) => {
  const encodedPartNo = encodeURIComponent(partNo || "");
  return encodedPartNo ? `/posts/${encodedPartNo}` : "/posts";
};

const buildPostStateFromSlug = (carSlug) => {
  const normalizedCarSlug = String(carSlug || "").trim();
  return normalizedCarSlug ? { carSlug: normalizedCarSlug } : undefined;
};

const buildPostStateFromItem = (item) =>
  buildPostStateFromSlug(slugifyText(item?.car_name || ""));

const getPaginationRange = (currentPage, totalPages) => {
  const window = 2;
  const start = Math.max(1, currentPage - window);
  const end = Math.min(totalPages, currentPage + window);

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const buildPostPathFromItem = (item) => {
  return buildPostPathFromPartNo(item?.part_no || "");
};

const buildOrderPathFromItem = (item) => {
  const params = new URLSearchParams();

  if (item?.part_no) {
    params.set("part_no", item.part_no);
  }

  if (item?.part_name_vn || item?.part_name) {
    params.set("part_name_vn", item.part_name_vn || item.part_name);
  }

  if (item?.image_url) {
    params.set("image_url", item.image_url);
  }

  params.set("quantity", "1");
  params.set("add", "1");

  const query = params.toString();
  return query ? `/order?${query}` : "/order";
};

const normalizeCartItem = (item) => {
  const partNo = String(item?.part_no || "").trim();
  if (!partNo) {
    return null;
  }

  return {
    part_no: partNo,
    part_name_vn: String(item?.part_name_vn || item?.part_name || partNo).trim() || partNo,
    image_url: String(item?.image_url || "").trim(),
    quantity: Math.max(1, Number.parseInt(item?.quantity, 10) || 1),
  };
};

const readOrderCart = () => {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(ORDER_CART_STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.map(normalizeCartItem).filter(Boolean);
  } catch {
    return [];
  }
};

const writeOrderCart = (items) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(ORDER_CART_STORAGE_KEY, JSON.stringify(items.map(normalizeCartItem).filter(Boolean)));
};

const upsertOrderCartItem = (items, nextItem) => {
  const normalizedNextItem = normalizeCartItem(nextItem);
  if (!normalizedNextItem) {
    return items;
  }

  const existingIndex = items.findIndex((item) => item.part_no === normalizedNextItem.part_no);
  if (existingIndex === -1) {
    return [...items, normalizedNextItem];
  }

  return items.map((item, index) => {
    if (index !== existingIndex) {
      return item;
    }

    return {
      ...item,
      part_name_vn: normalizedNextItem.part_name_vn || item.part_name_vn,
      image_url: normalizedNextItem.image_url || item.image_url,
      quantity: item.quantity + normalizedNextItem.quantity,
    };
  });
};

const buildOrderNotes = (item, formData, totalItems) => {
  const noteParts = [];

  if (formData.address.trim()) {
    noteParts.push(`Địa chỉ giao hàng: ${formData.address.trim()}`);
  }

  if (totalItems > 1) {
    noteParts.push(`Đơn thuộc giỏ hàng gồm ${totalItems} sản phẩm.`);
  }

  if (formData.notes.trim()) {
    noteParts.push(`Ghi chú khách: ${formData.notes.trim()}`);
  }

  return noteParts.join(" | ") || `Đặt hàng cho phụ tùng ${item.part_no}`;
};

function RichPostEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const [selectionMenu, setSelectionMenu] = useState({ visible: false, top: 0, left: 0 });
  const DEFAULT_HEADING_RGB = "rgb(220, 38, 38)";
  const FONT_SIZE_OPTIONS = [
    { label: "12px", commandSize: "2", className: "rich-font-size-12" },
    { label: "14px", commandSize: "3", className: "rich-font-size-14" },
    { label: "16px", commandSize: "4", className: "rich-font-size-16" },
    { label: "18px", commandSize: "5", className: "rich-font-size-18" },
    { label: "20px", commandSize: "6", className: "rich-font-size-20" },
    { label: "24px", commandSize: "7", className: "rich-font-size-24" },
  ];
  const FONT_SIZE_CLASS_BY_COMMAND_SIZE = FONT_SIZE_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.commandSize]: option.className }),
    {},
  );

  const emitContent = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  const getEditorSelectionRange = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();

    if (!editor || !selection || selection.rangeCount === 0) {
      return null;
    }

    const range = selection.getRangeAt(0);
    return editor.contains(range.commonAncestorContainer) ? range.cloneRange() : null;
  };

  const restoreEditorSelection = (range) => {
    if (!range) {
      return false;
    }

    const selection = window.getSelection();
    if (!selection) {
      return false;
    }

    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  };

  const normalizeRgbColor = (input) => {
    const value = String(input || "").trim();
    if (!value) {
      return null;
    }

    const rgbMatch = value.match(/^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
    const csvMatch = value.match(/^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/);
    const channels = rgbMatch?.slice(1) || csvMatch?.slice(1);

    if (!channels) {
      return null;
    }

    const values = channels.map((channel) => Number(channel));
    if (values.some((channel) => Number.isNaN(channel) || channel < 0 || channel > 255)) {
      return null;
    }

    return `rgb(${values.join(", ")})`;
  };

  const exec = (command, arg) => {
    if (!editorRef.current) {
      return;
    }

    editorRef.current.focus();
    document.execCommand(command, false, arg);
    emitContent();
  };

  const addLink = () => {
    const url = window.prompt("Nhập đường dẫn (https://...)");
    if (!url) {
      return;
    }
    exec("createLink", url.trim());
  };

  const applyTextColor = () => {
    const savedRange = getEditorSelectionRange();
    if (!savedRange || savedRange.collapsed) {
      window.alert("Hãy bôi đen đoạn text cần đổi màu trước.");
      return;
    }

    const rawColor = window.prompt(
      "Nhập màu RGB. Ví dụ: 255,0,0 hoặc rgb(255,0,0)",
      "rgb(220, 38, 38)",
    );
    if (!rawColor) {
      return;
    }

    const normalizedColor = normalizeRgbColor(rawColor);
    if (!normalizedColor) {
      window.alert("Màu không hợp lệ. Hãy nhập theo dạng 255,0,0 hoặc rgb(255,0,0).");
      return;
    }

    editorRef.current?.focus();
    if (!restoreEditorSelection(savedRange)) {
      window.alert("Không thể khôi phục đoạn text đã chọn để tô màu.");
      return;
    }

    document.execCommand("styleWithCSS", false, true);
    document.execCommand("foreColor", false, normalizedColor);
    emitContent();
  };

  const applyHeadingWithColor = (tagName) => {
    const savedRange = getEditorSelectionRange();
    if (!savedRange) {
      window.alert("Hãy đặt con trỏ hoặc bôi đen đoạn text trước khi tạo heading.");
      return;
    }

    editorRef.current?.focus();
    if (!restoreEditorSelection(savedRange)) {
      window.alert("Không thể khôi phục vùng chọn để tạo heading.");
      return;
    }

    document.execCommand("formatBlock", false, `<${tagName}>`);
    document.execCommand("styleWithCSS", false, true);
    document.execCommand("foreColor", false, DEFAULT_HEADING_RGB);
    emitContent();
  };

  const normalizeFontTags = () => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }

    editor.querySelectorAll("font[size]").forEach((fontNode) => {
      const size = fontNode.getAttribute("size") || "";
      const className = FONT_SIZE_CLASS_BY_COMMAND_SIZE[size];

      const span = document.createElement("span");
      if (className) {
        span.className = className;
      }
      span.innerHTML = fontNode.innerHTML;
      fontNode.replaceWith(span);
    });
  };

  const applyFontSize = (event) => {
    const selectedClass = event.target.value;
    if (!selectedClass || !editorRef.current) {
      return;
    }

    const selectedOption = FONT_SIZE_OPTIONS.find((option) => option.className === selectedClass);
    if (!selectedOption) {
      return;
    }

    editorRef.current.focus();
    document.execCommand("fontSize", false, selectedOption.commandSize);
    normalizeFontTags();
    emitContent();
  };

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }

    const html = value || "";
    if (editorRef.current.innerHTML !== html) {
      editorRef.current.innerHTML = html;
    }
  }, [value]);

  useEffect(() => {
    const updateSelectionMenu = () => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        setSelectionMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
        return;
      }

      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) {
        setSelectionMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
        return;
      }

      const rect = range.getBoundingClientRect();
      const hostRect = editor.getBoundingClientRect();
      setSelectionMenu({
        visible: true,
        top: Math.max(-16, rect.top - hostRect.top - 46),
        left: Math.max(8, rect.left - hostRect.left),
      });
    };

    document.addEventListener("selectionchange", updateSelectionMenu);
    return () => {
      document.removeEventListener("selectionchange", updateSelectionMenu);
    };
  }, []);

  return (
    <div className="rich-editor-shell">
      <div className="rich-editor-toolbar" role="toolbar" aria-label="Định dạng bài viết">
        <button type="button" className="rich-editor-btn" onClick={() => exec("formatBlock", "<h2>")}>H2</button>
        <button type="button" className="rich-editor-btn" onClick={() => exec("formatBlock", "<h3>")}>H3</button>
        <button type="button" className="rich-editor-btn" onClick={() => applyHeadingWithColor("h2")}>H2 đỏ</button>
        <button type="button" className="rich-editor-btn" onClick={() => applyHeadingWithColor("h3")}>H3 đỏ</button>
        <select className="rich-editor-size-select" defaultValue="" onChange={applyFontSize} aria-label="Chọn cỡ chữ">
          <option value="" disabled>Size</option>
          {FONT_SIZE_OPTIONS.map((option) => (
            <option key={option.className} value={option.className}>{option.label}</option>
          ))}
        </select>
        <button type="button" className="rich-editor-btn" onClick={() => exec("bold")}>B</button>
        <button type="button" className="rich-editor-btn" onClick={() => exec("italic")}>I</button>
        <button type="button" className="rich-editor-btn" onClick={applyTextColor}>RGB</button>
        <button
          type="button"
          className="rich-editor-btn"
          onClick={() => exec("insertUnorderedList")}
          aria-label="Tạo bullet list"
          title="Tạo bullet list"
        >
          Bullet List
        </button>
        <button type="button" className="rich-editor-btn" onClick={() => exec("formatBlock", "<blockquote>")}>Quote</button>
        <button type="button" className="rich-editor-btn" onClick={() => exec("hiliteColor", "#fff4a8")}>Highlight</button>
        <button type="button" className="rich-editor-btn" onClick={addLink}>Link</button>
        <button type="button" className="rich-editor-btn" onClick={() => exec("removeFormat")}>Clear</button>
      </div>

      <div className="rich-editor-grid">
        <div className="rich-editor-write-zone">
          <div className="rich-editor-canvas-wrap">
            {selectionMenu.visible && (
              <div className="rich-editor-selection-menu" style={{ top: selectionMenu.top, left: selectionMenu.left }}>
                <button type="button" className="rich-editor-mini-btn" onClick={() => exec("bold")}>B</button>
                <button type="button" className="rich-editor-mini-btn" onClick={() => exec("italic")}>I</button>
                <button type="button" className="rich-editor-mini-btn" onClick={() => exec("hiliteColor", "#fff4a8")}>✦</button>
              </div>
            )}

            <div
              ref={editorRef}
              className="rich-editor-canvas"
              contentEditable
              suppressContentEditableWarning
              data-placeholder={placeholder}
              onInput={emitContent}
            />
          </div>
        </div>

        <aside className="rich-editor-preview-card">
          <p className="rich-editor-preview-kicker">Live preview</p>
          <article className="post-detail-card rich-editor-preview-body">
            <div
              className="post-detail-body"
              dangerouslySetInnerHTML={{ __html: stripStyleAttributes(value) || "<p>Nhập nội dung để xem preview...</p>" }}
            />
          </article>
        </aside>
      </div>
    </div>
  );
}

function Home() {
  const navigate = useNavigate();
  const [groupPages, setGroupPages] = useState({
    bestSeller: 1,
    newParts: 1,
    supplies: 1,
  });
  const [groupPayload, setGroupPayload] = useState(() => {
    const initialPages = {
      bestSeller: 1,
      newParts: 1,
      supplies: 1,
    };

    return getCachedHomeGroupsPayload(getHomeGroupsCacheKey(8, initialPages));
  });
  const [loading, setLoading] = useState(false);
  const [loadingGroupKey, setLoadingGroupKey] = useState(null);
  const [error, setError] = useState("");
  const [homeSearchQuery, setHomeSearchQuery] = useState("");
  const pageSize = 8;
  const isInitialLoading = loading && !groupPayload;

  useEffect(() => {
    let cancelled = false;
    const cacheKey = getHomeGroupsCacheKey(pageSize, groupPages);
    const cachedPayload = getCachedHomeGroupsPayload(cacheKey);

    const fetchGroupedPosts = async () => {
      if (cachedPayload) {
        setGroupPayload(cachedPayload);
        setLoading(false);
        setLoadingGroupKey(null);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          pageSize: String(pageSize),
          bestSellerPage: String(groupPages.bestSeller),
          newPartsPage: String(groupPages.newParts),
          suppliesPage: String(groupPages.supplies),
        });

        const response = await fetch(`/api/home-post-groups?${params.toString()}`);
        const payload = await readJsonSafe(response);

        if (!response.ok) {
          throw new Error(
            getApiErrorMessage(payload, "Không thể tải dữ liệu trang chủ."),
          );
        }

        if (!cancelled) {
          setGroupPayload(payload);
          setCachedHomeGroupsPayload(cacheKey, payload);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError.message || "Không thể tải dữ liệu trang chủ.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingGroupKey(null);
        }
      }
    };

    fetchGroupedPosts();

    return () => {
      cancelled = true;
    };
  }, [groupPages, pageSize]);

  const groups = groupPayload?.groups || {};

  const handleGroupPageChange = (groupKey, pageNumber) => {
    if (pageNumber === groupPages[groupKey]) {
      return;
    }

    setLoadingGroupKey(groupKey);
    setGroupPages((prev) => ({
      ...prev,
      [groupKey]: pageNumber,
    }));
  };

  const handleHomeSearchSubmit = (event) => {
    event.preventDefault();
    const normalizedQuery = homeSearchQuery.trim();

    if (!normalizedQuery) {
      return;
    }

    navigate(`/search?q=${encodeURIComponent(normalizedQuery)}`);
  };

  return (
    <main className="home-market-page">
      <div className="home-market-layout">
        <ResponsiveSidebar />

        <section className="home-market-right">
          <section className="home-mobile-search-hero" aria-label="Tìm kiếm thông tin phụ tùng">
            <div className="home-mobile-search-hero-copy">
              <p className="home-mobile-search-kicker">Tìm kiếm nhanh</p>
              <h1>Tìm thông tin phụ tùng phù hợp cho xe của bạn</h1>
            </div>
            <form className="home-mobile-search-form" onSubmit={handleHomeSearchSubmit}>
              <input
                type="text"
                className="home-mobile-search-input"
                placeholder="Nhập mã phụ tùng, tên phụ tùng hoặc từ khóa"
                value={homeSearchQuery}
                onChange={(event) => setHomeSearchQuery(event.target.value)}
                aria-label="Nhập thông tin phụ tùng"
              />
              <button type="submit" className="home-mobile-search-button">Tìm kiếm</button>
            </form>
          </section>

          {isInitialLoading && HOME_POST_GROUPS.map((groupDef) => (
            <section key={`skeleton-${groupDef.key}`} className="home-post-group" aria-label={`${groupDef.title} đang tải`}>
              <div className="home-post-group-title-row">
                <h2>{groupDef.title}</h2>
              </div>

              <div className="home-product-grid home-product-grid-skeleton" aria-hidden="true">
                {HOME_SKELETON_ITEMS.map((itemIndex) => (
                  <article key={`${groupDef.key}-skeleton-${itemIndex}`} className="home-product-card home-product-card-skeleton">
                    <div className="home-product-image-skeleton home-skeleton-block" />
                    <div className="home-product-price-skeleton home-skeleton-block" />
                    <div className="home-product-name-skeleton">
                      <span className="home-skeleton-block" />
                      <span className="home-skeleton-block" />
                    </div>
                    <div className="home-product-buy-skeleton home-skeleton-block" />
                  </article>
                ))}
              </div>
            </section>
          ))}
          {!isInitialLoading && error && <p className="home-group-message error">{error}</p>}

          {!isInitialLoading && !error && HOME_POST_GROUPS.map((groupDef) => {
            const group = groups[groupDef.key] || {};
            const items = Array.isArray(group.items) ? group.items : [];
            const totalPages = Math.max(1, Number(group.totalPages || 1));
            const activePage = Number(group.page || groupPages[groupDef.key] || 1);
            const isGroupLoading = loadingGroupKey === groupDef.key;

            return (
              <section key={groupDef.key} className={`home-post-group ${isGroupLoading ? "home-post-group-loading" : ""}`.trim()} aria-label={groupDef.title}>
                <div className="home-post-group-title-row">
                  <h2>{groupDef.title}</h2>
                  {isGroupLoading && <span className="home-post-group-status">Đang tải...</span>}
                </div>

                {items.length === 0 && (
                  <p className="home-group-message">Chưa có dữ liệu cho nhóm này.</p>
                )}

                {items.length > 0 && (
                  <>
                    {isGroupLoading && <div className="home-post-group-overlay" aria-hidden="true" />}
                    <div className="home-product-grid">
                      {items.map((item) => (
                        <article key={`${groupDef.key}-${item.part_no}`} className="home-product-card">
                          <Link to={buildPostPathFromItem(item)} state={buildPostStateFromItem(item)} className="home-product-image-link" aria-label={`Xem bài viết ${item.part_no}`}>
                            <img
                              src={item.image_url || "/images/Phu_tung_o_to_Hyundai_chinh_hang.webp"}
                              alt={item.image_alt || item.part_name_vn || item.part_name || item.part_no}
                              loading="lazy"
                              decoding="async"
                              className="home-product-image"
                            />
                          </Link>
                          <p className="home-product-price">Phụ tùng: {item.part_no}</p>
                          <h3 className="home-product-name">{item.part_name_vn || item.part_name || item.part_no || "Phụ tùng"}</h3>
                          <div className="home-product-actions">
                            <Link to={buildPostPathFromItem(item)} state={buildPostStateFromItem(item)} className="home-product-buy">Xem thêm</Link>
                            <Link to={buildOrderPathFromItem(item)} className="home-product-buy home-product-buy-order">Mua hàng</Link>
                          </div>
                        </article>
                      ))}
                    </div>

                    <div className="home-product-pagination">
                      <button
                        className="home-page-btn home-page-prev"
                        disabled={activePage === 1 || isGroupLoading}
                        onClick={() => handleGroupPageChange(groupDef.key, activePage - 1)}
                        aria-label="Trang trước"
                      >
                        ← Trước
                      </button>

                      {getPaginationRange(activePage, totalPages).map((pageNumber) => (
                        <button
                          key={`${groupDef.key}-page-${pageNumber}`}
                          className={`home-page-btn ${pageNumber === activePage ? "home-page-btn-active" : ""}`}
                          disabled={isGroupLoading}
                          onClick={() => handleGroupPageChange(groupDef.key, pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      ))}

                      <button
                        className="home-page-btn home-page-next"
                        disabled={activePage === totalPages || isGroupLoading}
                        onClick={() => handleGroupPageChange(groupDef.key, activePage + 1)}
                        aria-label="Trang sau"
                      >
                        Sau →
                      </button>
                    </div>
                  </>
                )}
              </section>
            );
          })}
        </section>
      </div>
    </main>
  );
}

function About() {
  return (
    <main className="App-content about-page">
      <h1 className="about-title">Phụ tùng ô tô chính hãng - Phutungoto.shop</h1>

      <section className="about-section">
        <h2 className="about-section-title">Giới thiệu</h2>
        <p>
          <strong>Phutungoto.shop</strong> là địa chỉ cung cấp <strong>phụ tùng ô tô chính hãng</strong>,
          hướng tới sự minh bạch về nguồn gốc, tư vấn đúng nhu cầu sử dụng và đồng hành lâu dài cùng
          khách hàng trong quá trình bảo dưỡng, sửa chữa xe.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section-title">Nền tảng kinh nghiệm</h2>
        <p>
          Đội ngũ của chúng tôi có những nhân sự với <strong>hơn 15 năm kinh nghiệm</strong> trong lĩnh vực
          kinh doanh phụ tùng ô tô chính hãng, sửa chữa và bảo trì nhiều dòng xe phổ biến tại Việt Nam như
          <strong> Hyundai, Toyota, Honda, Mercedes, Mitsubishi, Kia, BMW, Ford</strong> và nhiều dòng xe khác.
        </p>
        <p>
          Kinh nghiệm thực tế này giúp chúng tôi hiểu rõ nhu cầu phụ tùng, khả năng tương thích theo từng đời xe,
          cũng như những lưu ý quan trọng trong quá trình thay thế và vận hành, từ đó mang lại sự yên tâm hơn cho khách hàng.
        </p>
      </section>

      <section className="about-section">
        <h2 className="about-section-title">Giá trị chúng tôi theo đuổi</h2>
        <p className="about-highlight">
          Chúng tôi ưu tiên xây dựng niềm tin bằng việc tư vấn rõ ràng, hạn chế nhầm lẫn mã phụ tùng, hỗ trợ tra cứu
          thông tin theo xe và theo mã, đồng thời luôn hướng khách hàng tới lựa chọn phù hợp với nhu cầu sử dụng thực tế.
        </p>

        <ul>
          <li>Ưu tiên phụ tùng có nguồn gốc rõ ràng và đúng tiêu chuẩn nhà sản xuất</li>
          <li>Tư vấn theo mã phụ tùng, dòng xe, đời xe và tình trạng sử dụng thực tế</li>
          <li>Đồng hành trước, trong và sau khi khách hàng đặt mua sản phẩm</li>
        </ul>
      </section>

      <section className="about-section">
        <h2 className="about-section-title">Thông tin liên hệ</h2>
        <ul className="about-contact-list">
          <li><strong>Hotline:</strong> 0388 861 168</li>
          <li><strong>Email:</strong> {CONTACT_EMAIL}</li>
          <li><strong>Địa chỉ:</strong> {CONTACT_ADDRESS}</li>
        </ul>
      </section>
    </main>
  );
}

function Order() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cartItems, setCartItems] = useState(() => readOrderCart());
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    notes: "",
  });
  const [quickAddForm, setQuickAddForm] = useState({
    part_no: "",
    part_name_vn: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [cartMessage, setCartMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    writeOrderCart(cartItems);
  }, [cartItems]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("add") || !params.get("part_no")) {
      return;
    }

    const incomingItem = normalizeCartItem({
      part_no: params.get("part_no"),
      part_name_vn: params.get("part_name_vn"),
      image_url: params.get("image_url"),
      quantity: params.get("quantity") || 1,
    });

    if (!incomingItem) {
      return;
    }

    setCartItems((prev) => upsertOrderCartItem(prev, incomingItem));
    setCartMessage(`Đã thêm ${incomingItem.part_name_vn} vào giỏ hàng.`);
    setSuccess("");
    setErrors({});
    navigate("/order", { replace: true });
  }, [location.search, navigate]);

  const totalQuantity = useMemo(
    () => cartItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [cartItems],
  );

  const validate = () => {
    const newErrors = {};

    if (cartItems.length === 0) {
      newErrors.cart = "Giỏ hàng đang trống. Vui lòng chọn ít nhất một phụ tùng.";
    }
    if (!formData.name.trim()) newErrors.name = "Vui lòng nhập tên.";
    if (!formData.phone.trim())
      newErrors.phone = "Vui lòng nhập số điện thoại.";
    if (!/^[0-9]{8,15}$/.test(formData.phone.trim()))
      newErrors.phone = "Số điện thoại phải từ 8 đến 15 chữ số.";
    if (!formData.email.trim()) newErrors.email = "Vui lòng nhập email.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))
      newErrors.email = "Email không hợp lệ.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess("");
    setCartMessage("");

    if (!validate()) return;

    setSubmitting(true);

    try {
      const createdOrderIds = [];

      for (const item of cartItems) {
        const response = await fetch("/api/orders", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            part_no: item.part_no,
            part_name_vn: item.part_name_vn,
            quantity: item.quantity,
            notes: buildOrderNotes(item, formData, cartItems.length),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          setErrors({ api: data.error || "Lỗi khi gửi đơn hàng." });
          setSubmitting(false);
          return;
        }

        if (data.orderId) {
          createdOrderIds.push(data.orderId);
        }
      }

      setSuccess(
        createdOrderIds.length > 0
          ? `Đã gửi ${cartItems.length} sản phẩm. Mã đơn: ${createdOrderIds.join(", ")}`
          : `Đã gửi ${cartItems.length} sản phẩm thành công.`,
      );
      setCartItems([]);
      setFormData({
        name: "",
        phone: "",
        email: "",
        address: "",
        notes: "",
      });
      setErrors({});
    } catch (error) {
      console.error("Order submit error:", error);
      setErrors({ api: "Không thể kết nối đến server. Vui lòng thử lại." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuantityChange = (partNo, quantityValue) => {
    const normalizedQuantity = Math.max(1, Number.parseInt(quantityValue, 10) || 1);

    setCartItems((prev) => prev.map((item) => (
      item.part_no === partNo ? { ...item, quantity: normalizedQuantity } : item
    )));
  };

  const handleRemoveItem = (partNo) => {
    setCartItems((prev) => prev.filter((item) => item.part_no !== partNo));
    setCartMessage("Đã xóa sản phẩm khỏi giỏ hàng.");
    setSuccess("");
  };

  const handleClearCart = () => {
    setCartItems([]);
    setCartMessage("Giỏ hàng đã được làm trống.");
    setSuccess("");
  };

  const handleQuickAddChange = (event) => {
    const { name, value } = event.target;
    setQuickAddForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuickAddSubmit = (event) => {
    event.preventDefault();

    const partNo = quickAddForm.part_no.trim();
    const partName = quickAddForm.part_name_vn.trim();

    if (!partNo || !partName) {
      setCartMessage("Nhập mã phụ tùng và tên phụ tùng để thêm vào giỏ hàng.");
      return;
    }

    setCartItems((prev) => upsertOrderCartItem(prev, {
      part_no: partNo,
      part_name_vn: partName,
      quantity: 1,
    }));
    setQuickAddForm({ part_no: "", part_name_vn: "" });
    setCartMessage(`Đã thêm ${partName} vào giỏ hàng.`);
    setSuccess("");
  };

  const handleCartRefresh = () => {
    writeOrderCart(cartItems);
    setCartMessage("Giỏ hàng đã được cập nhật.");
    setSuccess("");
  };

  return (
    <main className="App-content order-page">
      <section className="order-page-hero">
        <div>
          <p className="order-page-kicker">Giỏ hàng và đặt hàng</p>
          <p className="order-page-description">
            Kiểm tra sản phẩm đã chọn, điều chỉnh số lượng và để lại thông tin giao hàng.
            Tư vấn viên sẽ xác nhận lại báo giá và tình trạng hàng trước khi chốt đơn.
          </p>
        </div>

        <div className="order-page-stats" aria-label="Tóm tắt giỏ hàng">
          <div className="order-page-stat-card">
            <strong>{cartItems.length}</strong>
            <span>Sản phẩm</span>
          </div>
          <div className="order-page-stat-card">
            <strong>{totalQuantity}</strong>
            <span>Tổng số lượng</span>
          </div>
        </div>
      </section>

      {(errors.cart || errors.api || cartMessage || success) && (
        <div className="order-alert-stack">
          {errors.cart && <p className="order-alert order-alert-error">{errors.cart}</p>}
          {errors.api && <p className="order-alert order-alert-error">{errors.api}</p>}
          {cartMessage && <p className="order-alert order-alert-info">{cartMessage}</p>}
          {success && <p className="order-alert order-alert-success">{success}</p>}
        </div>
      )}

      <div className="order-layout">
        <section className="order-cart-card">
          <div className="order-card-header">
            <div>
              <p className="order-card-kicker">Sản phẩm đã chọn</p>
              <h2>Giỏ hàng</h2>
            </div>
            <span className="order-card-chip">Báo giá xác nhận sau</span>
          </div>

          <form className="order-quick-add-form" onSubmit={handleQuickAddSubmit}>
            <input
              type="text"
              name="part_no"
              value={quickAddForm.part_no}
              onChange={handleQuickAddChange}
              placeholder="Mã phụ tùng"
            />
            <input
              type="text"
              name="part_name_vn"
              value={quickAddForm.part_name_vn}
              onChange={handleQuickAddChange}
              placeholder="Tên phụ tùng"
            />
            <button type="submit" className="order-action-button order-action-primary">
              Thêm sản phẩm
            </button>
          </form>

          <div className="order-cart-table-wrapper">
            <table className="order-cart-table">
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Mã sản phẩm</th>
                  <th>Tên sản phẩm</th>
                  <th>Số lượng</th>
                  <th>Thành tiền</th>
                  <th>Hành động</th>
                </tr>
              </thead>
              <tbody>
                {cartItems.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="order-cart-empty-cell">
                      Giỏ hàng trống
                    </td>
                  </tr>
                ) : (
                  cartItems.map((item, index) => (
                      <tr key={item.part_no}>
                        <td>{index + 1}</td>
                        <td>
                          <span className="order-cart-code">{item.part_no}</span>
                        </td>
                        <td>
                          <div className="order-cart-product">
                            <strong>{item.part_name_vn || item.part_name || item.part_no}</strong>
                          </div>
                        </td>
                        <td>
                          <input
                            className="order-cart-qty"
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(event) => handleQuantityChange(item.part_no, event.target.value)}
                          />
                        </td>
                        <td>
                          <span className="order-price-badge">Liên hệ báo giá</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="order-remove-button"
                            onClick={() => handleRemoveItem(item.part_no)}
                          >
                            Xóa
                          </button>
                        </td>
                      </tr>
                    ))
                )}

                <tr className="order-cart-summary-row">
                  <td colSpan="3" />
                  <td className="order-summary-label">Tổng tiền:</td>
                  <td>
                    <span className="order-cart-total-note">Liên hệ báo giá</span>
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          {cartItems.length > 0 && (
              <div className="order-cart-actions">
                <button type="button" className="order-action-button order-action-primary" onClick={handleCartRefresh}>
                  Cập nhật giỏ hàng
                </button>
                <button type="button" className="order-action-button order-action-secondary" onClick={handleClearCart}>
                  Làm trống giỏ hàng
                </button>
              </div>
          )}
        </section>

        <section className="order-checkout-card">
          <div className="order-card-header">
            <div>
              <p className="order-card-kicker">Thông tin giao hàng</p>
              <h2>Đặt hàng</h2>
            </div>
          </div>

          <form className="order-form order-checkout-form" onSubmit={handleSubmit} noValidate>
            <div className="order-field-grid">
              <label>
                Tên đầy đủ
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Nguyễn Văn A"
                />
                {errors.name && <small className="error">{errors.name}</small>}
              </label>

              <label>
                Điện thoại
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="0388861168"
                />
                {errors.phone && <small className="error">{errors.phone}</small>}
              </label>
            </div>

            <label>
              Email
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="info@phutungoto.shop"
              />
              {errors.email && <small className="error">{errors.email}</small>}
            </label>

            <label>
              Địa chỉ giao hàng
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="3"
                placeholder="Số nhà, đường, phường/xã, quận/huyện, tỉnh/thành"
              />
            </label>

            <label>
              Ghi chú
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                placeholder="Ví dụ: cần xuất hóa đơn, giao giờ hành chính, xác nhận phụ tùng theo VIN..."
              />
            </label>

            <button type="submit" className="btn-primary order-submit-button" disabled={submitting || cartItems.length === 0}>
              {submitting ? "Đang gửi đơn..." : "Gửi đơn hàng"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}

function SearchParts() {
  const location = useLocation();
  const keywordPageSize = 12;

  const [sidebarResults, setSidebarResults] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarError, setSidebarError] = useState("");
  const [sidebarPage, setSidebarPage] = useState(1);
  const [keywordResults, setKeywordResults] = useState([]);
  const [keywordLoading, setKeywordLoading] = useState(false);
  const [keywordError, setKeywordError] = useState("");
  const [keywordMessage, setKeywordMessage] = useState("");
  const [keywordMatchType, setKeywordMatchType] = useState("none");
  const [keywordPage, setKeywordPage] = useState(1);
  const [searchRelatedItems, setSearchRelatedItems] = useState([]);
  const [searchRelatedLoading, setSearchRelatedLoading] = useState(false);
  const [searchRelatedError, setSearchRelatedError] = useState("");

  const sidebarCarName = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get("carName") || "").trim();
  }, [location.search]);

  const sidebarCategory = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get("category") || "").trim();
  }, [location.search]);

  const hasSidebarSelection = Boolean(sidebarCarName && sidebarCategory);
  const singleSidebarResult = useMemo(
    () => (sidebarResults.length === 1 ? sidebarResults[0] : null),
    [sidebarResults],
  );
  const singleSidebarCarSlug = useMemo(
    () => slugifyText(singleSidebarResult?.car_name || ""),
    [singleSidebarResult],
  );

  const keywordQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return String(params.get("q") || "").trim();
  }, [location.search]);

  React.useEffect(() => {
    if (!hasSidebarSelection) {
      setSidebarResults([]);
      setSidebarLoading(false);
      setSidebarError("");
      return;
    }

    let cancelled = false;

    const loadSidebarResults = async () => {
      setSidebarLoading(true);
      setSidebarError("");

      try {
        const params = new URLSearchParams({
          carName: sidebarCarName,
          category: sidebarCategory,
        });
        const response = await fetch(`/api/parts/by-car-category?${params.toString()}`);
        const payload = await readJsonSafe(response);

        if (!response.ok) {
          throw new Error(
            getApiErrorMessage(payload, "Lỗi lấy liên kết phụ tùng theo xe và nhóm."),
          );
        }

        if (cancelled) {
          return;
        }

        setSidebarResults(Array.isArray(payload.items) ? payload.items : []);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setSidebarResults([]);
        setSidebarError(
          error.message || "Lỗi lấy liên kết phụ tùng theo xe và nhóm.",
        );
      } finally {
        if (!cancelled) {
          setSidebarLoading(false);
        }
      }
    };

    loadSidebarResults();

    return () => {
      cancelled = true;
    };
  }, [hasSidebarSelection, sidebarCarName, sidebarCategory]);

  React.useEffect(() => {
    if (!sidebarCarName) {
      setSearchRelatedItems([]);
      setSearchRelatedError("");
      return;
    }
    let cancelled = false;
    const loadSearchRelated = async () => {
      setSearchRelatedLoading(true);
      setSearchRelatedError("");
      try {
        const response = await fetch(
          `/api/search/related-by-car?carName=${encodeURIComponent(sidebarCarName)}`,
        );
        const payload = await readJsonSafe(response);
        if (!cancelled && response.ok) {
          setSearchRelatedItems(Array.isArray(payload.items) ? payload.items : []);
        } else if (!cancelled) {
          setSearchRelatedError(payload?.error || "Không thể tải phụ tùng liên quan.");
          setSearchRelatedItems([]);
        }
      } catch {
        if (!cancelled) {
          setSearchRelatedError("Không thể tải phụ tùng liên quan.");
          setSearchRelatedItems([]);
        }
      } finally {
        if (!cancelled) setSearchRelatedLoading(false);
      }
    };
    loadSearchRelated();
    return () => { cancelled = true; };
  }, [sidebarCarName]);

  React.useEffect(() => {
    setSidebarPage(1);
  }, [sidebarCarName, sidebarCategory]);

  React.useEffect(() => {
    setKeywordPage(1);
  }, [keywordQuery]);

  React.useEffect(() => {
    if (!keywordQuery) {
      setKeywordResults([]);
      setKeywordLoading(false);
      setKeywordError("");
      setKeywordMessage("");
      setKeywordMatchType("none");
      return;
    }

    if (isTooShortAlphabeticSearchQuery(keywordQuery)) {
      setKeywordResults([]);
      setKeywordLoading(false);
      setKeywordMatchType("none");
      setKeywordMessage("");
      setKeywordError("Nhập mã phụ tùng hoặc tên phụ tùng cụ thể hơn");
      return;
    }

    let cancelled = false;

    const loadKeywordResults = async () => {
      setKeywordLoading(true);
      setKeywordError("");
      setKeywordMessage("");

      try {
        const response = await fetch(
          `/api/parts/keyword-search?q=${encodeURIComponent(keywordQuery)}`,
        );
        const payload = await readJsonSafe(response);
        if (!response.ok) {
          throw new Error(payload.error || "Lỗi tìm kiếm phụ tùng theo từ khóa");
        }

        if (cancelled) return;

        const items = Array.isArray(payload.items) ? payload.items : [];
        const nextMatchType = payload.matchType || "none";

        setKeywordResults(items);
        setKeywordMatchType(nextMatchType);

        if (nextMatchType === "exact") {
          setKeywordMessage(`Tìm thấy ${items.length} kết quả khớp với từ khóa.`);
        } else if (nextMatchType === "suggested") {
          setKeywordMessage("Không có kết quả khớp tuyệt đối. Một số kết quả gần đúng:");
        } else {
          setKeywordMessage("Không tìm thấy kết quả hoặc gợi ý phù hợp cho từ khóa này.");
        }
      } catch (err) {
        if (cancelled) return;
        setKeywordResults([]);
        setKeywordMatchType("none");
        setKeywordError(err.message || "Lỗi tìm kiếm phụ tùng theo từ khóa.");
      } finally {
        if (!cancelled) {
          setKeywordLoading(false);
        }
      }
    };

    loadKeywordResults();

    return () => {
      cancelled = true;
    };
  }, [keywordQuery]);

  const keywordTotalPages = useMemo(
    () => Math.max(1, Math.ceil(keywordResults.length / keywordPageSize)),
    [keywordResults.length, keywordPageSize],
  );

  React.useEffect(() => {
    setKeywordPage((prevPage) => Math.min(prevPage, keywordTotalPages));
  }, [keywordTotalPages]);

  const paginatedKeywordResults = useMemo(() => {
    const startIndex = (keywordPage - 1) * keywordPageSize;
    return keywordResults.slice(startIndex, startIndex + keywordPageSize);
  }, [keywordPage, keywordPageSize, keywordResults]);

  const sidebarTotalPages = useMemo(
    () => Math.max(1, Math.ceil(sidebarResults.length / keywordPageSize)),
    [sidebarResults.length, keywordPageSize],
  );

  React.useEffect(() => {
    setSidebarPage((prevPage) => Math.min(prevPage, sidebarTotalPages));
  }, [sidebarTotalPages]);

  const paginatedSidebarResults = useMemo(() => {
    const startIndex = (sidebarPage - 1) * keywordPageSize;
    return sidebarResults.slice(startIndex, startIndex + keywordPageSize);
  }, [keywordPageSize, sidebarPage, sidebarResults]);


  return (
    <main className="home-market-page search-page-layout">
      <div className="home-market-layout search-market-layout">
        <ResponsiveSidebar />

        <section className="home-market-right search-market-right">
          <div className="App-content post-content-shell">
            {hasSidebarSelection && (
              <section className="search-keyword-panel">
                <div className="search-keyword-header">
                  <p className="search-keyword-inline-title">
                    Kết quả theo xe và nhóm phụ tùng: <strong>{sidebarCarName}</strong> · <strong>{sidebarCategory}</strong>
                  </p>
                </div>

                {sidebarLoading && <p className="no-data">Đang tải liên kết phụ tùng...</p>}
                {sidebarError && <div className="alert alert-error">{sidebarError}</div>}

                {!sidebarLoading && !sidebarError && sidebarResults.length > 0 && (
                  <div className="alert alert-success">
                    Tìm thấy {sidebarResults.length} kết quả khớp với xe và nhóm phụ tùng.
                  </div>
                )}

                {!sidebarLoading && !sidebarError && sidebarResults.length === 1 && singleSidebarResult && (
                  <div className="search-single-post-wrap">
                    <PostDetailContent
                      partNo={singleSidebarResult.part_no}
                      carSlug={singleSidebarCarSlug}
                      embedded
                      relatedItemsOverride={searchRelatedItems}
                      relatedLoadingOverride={searchRelatedLoading}
                      relatedErrorOverride={searchRelatedError}
                    />
                  </div>
                )}

                {!sidebarLoading && !sidebarError && sidebarResults.length > 1 && (
                  <>
                    <div className="home-product-grid search-sidebar-product-grid" aria-live="polite">
                      {paginatedSidebarResults.map((item, index) => (
                        <article
                          key={`${item.part_no || "part"}-${item.post_link || (sidebarPage - 1) * keywordPageSize + index}`}
                          className="home-product-card search-sidebar-product-card"
                        >
                          <Link
                            to={buildPostPathFromItem(item)}
                            state={buildPostStateFromItem(item)}
                            className="home-product-image-link"
                            aria-label={`Xem bài viết ${item.part_no}`}
                          >
                            <img
                              src={item.image_url || "/images/Phu_tung_o_to_Hyundai_chinh_hang.webp"}
                              alt={item.image_alt || item.part_name_vn || item.part_name || item.part_no}
                              loading="lazy"
                              decoding="async"
                              className="home-product-image"
                            />
                          </Link>
                          <p className="home-product-price">Phụ tùng: {item.part_no}</p>
                          <h3 className="home-product-name">{item.part_name_vn || item.part_name || item.part_no || "Phụ tùng"}</h3>
                          <div className="home-product-actions search-sidebar-product-actions">
                            <Link to={buildPostPathFromItem(item)} state={buildPostStateFromItem(item)} className="home-product-buy">
                              Xem thêm
                            </Link>

                            <Link to={buildOrderPathFromItem(item)} className="home-product-buy home-product-buy-order">
                              Mua hàng
                            </Link>
                          </div>
                        </article>
                      ))}
                    </div>

                    {sidebarTotalPages > 1 && (
                      <div className="search-keyword-pagination-wrap">
                        <p className="search-keyword-pagination-summary">
                          Trang {sidebarPage}/{sidebarTotalPages} · {sidebarResults.length} kết quả
                        </p>

                        <div className="search-keyword-pagination">
                          <button
                            className="home-page-btn home-page-prev"
                            disabled={sidebarPage === 1}
                            onClick={() => setSidebarPage((prevPage) => Math.max(1, prevPage - 1))}
                            aria-label="Trang kết quả trước"
                          >
                            ← Trước
                          </button>

                          {getPaginationRange(sidebarPage, sidebarTotalPages).map((pageNumber) => (
                            <button
                              key={`sidebar-page-${pageNumber}`}
                              className={`home-page-btn ${pageNumber === sidebarPage ? "home-page-btn-active" : ""}`}
                              onClick={() => setSidebarPage(pageNumber)}
                              aria-label={`Trang kết quả ${pageNumber}`}
                            >
                              {pageNumber}
                            </button>
                          ))}

                          <button
                            className="home-page-btn home-page-next"
                            disabled={sidebarPage === sidebarTotalPages}
                            onClick={() => setSidebarPage((prevPage) => Math.min(sidebarTotalPages, prevPage + 1))}
                            aria-label="Trang kết quả sau"
                          >
                            Sau →
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {!sidebarLoading && !sidebarError && sidebarResults.length === 0 && (
                  <p className="no-data">
                    Không tìm thấy liên kết bài viết phù hợp cho xe và nhóm phụ tùng này.
                  </p>
                )}
              </section>
            )}

            {keywordQuery && (
              <section className="search-keyword-panel">
                <div className="search-keyword-header">
                  <p className="search-keyword-inline-title">
                    Kết quả tìm kiếm theo từ khóa: <strong>{keywordQuery}</strong>
                  </p>
                </div>

                {keywordLoading && <p className="no-data">Đang tìm kiếm theo từ khóa...</p>}
                {keywordError && (
                  <div
                    className={`alert alert-error ${keywordError === "Nhập mã phụ tùng hoặc tên phụ tùng cụ thể hơn" ? "keyword-guidance-message" : ""}`.trim()}
                  >
                    {keywordError}
                  </div>
                )}
                {!keywordLoading && !keywordError && keywordMessage && (
                  <div
                    className={`alert ${keywordMatchType === "suggested" ? "alert-info keyword-suggested-message" : "alert-success"}`}
                  >
                    {keywordMessage}
                  </div>
                )}

                {!keywordLoading && !keywordError && keywordResults.length > 0 && (
                  <>
                    <div className="search-keyword-list" aria-live="polite">
                    {paginatedKeywordResults.map((item, index) => (
                      <article
                        key={`${item.part_no || "part"}-${item.post_link || (keywordPage - 1) * keywordPageSize + index}`}
                        className="search-keyword-card"
                      >
                        <div className="search-keyword-card-top">
                          <p className="search-keyword-partno">Phụ tùng: {item.part_no}</p>
                          {item.car_name ? (
                            <span className="search-keyword-car">{item.car_name}</span>
                          ) : null}
                        </div>
                        {item.has_article ? (
                          <Link
                            to={buildPostPathFromItem(item)}
                            state={buildPostStateFromItem(item)}
                            className="search-keyword-image-link"
                            aria-label={`Xem hình ảnh phụ tùng ${item.part_no}`}
                          >
                            <img
                              src={item.image_url || "/images/Phu_tung_o_to_Hyundai_chinh_hang.webp"}
                              alt={item.image_alt || item.part_name_vn || item.part_name || item.part_no || "Phụ tùng"}
                              loading="lazy"
                              decoding="async"
                              className="home-product-image search-keyword-image"
                            />
                          </Link>
                        ) : (
                          <div className="search-keyword-image-link search-keyword-image-placeholder">
                            <img
                              src={item.image_url || "/images/Phu_tung_o_to_Hyundai_chinh_hang.webp"}
                              alt={item.image_alt || item.part_name_vn || item.part_name || item.part_no || "Phụ tùng"}
                              loading="lazy"
                              decoding="async"
                              className="home-product-image search-keyword-image"
                            />
                          </div>
                        )}
                        <h3>{item.part_name_vn || item.part_no || "Phụ tùng"}</h3>
                        <div className="search-keyword-actions">
                          {item.has_article && item.post_link ? (
                            <Link to={buildPostPathFromItem(item)} state={buildPostStateFromItem(item)} className="search-keyword-link">
                              Chi tiết
                            </Link>
                          ) : null}

                          <Link to={buildOrderPathFromItem(item)} className="search-keyword-link search-keyword-order-link">
                            <svg viewBox="0 0 24 24" aria-hidden="true" className="search-keyword-link-icon">
                              <path
                                d="M3 5h2.2l1.2 6.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.74L19 7H7.1"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <circle cx="10" cy="18" r="1.5" fill="currentColor" />
                              <circle cx="17" cy="18" r="1.5" fill="currentColor" />
                            </svg>
                            Mua hàng
                          </Link>
                        </div>
                      </article>
                    ))}
                    </div>

                    {keywordTotalPages > 1 && (
                      <div className="search-keyword-pagination-wrap">
                        <p className="search-keyword-pagination-summary">
                          Trang {keywordPage}/{keywordTotalPages} · {keywordResults.length} kết quả
                        </p>

                        <div className="search-keyword-pagination">
                          <button
                            className="home-page-btn home-page-prev"
                            disabled={keywordPage === 1}
                            onClick={() => setKeywordPage((prevPage) => Math.max(1, prevPage - 1))}
                            aria-label="Trang kết quả trước"
                          >
                            ← Trước
                          </button>

                          {getPaginationRange(keywordPage, keywordTotalPages).map((pageNumber) => (
                            <button
                              key={`keyword-page-${pageNumber}`}
                              className={`home-page-btn ${pageNumber === keywordPage ? "home-page-btn-active" : ""}`}
                              onClick={() => setKeywordPage(pageNumber)}
                              aria-label={`Trang kết quả ${pageNumber}`}
                            >
                              {pageNumber}
                            </button>
                          ))}

                          <button
                            className="home-page-btn home-page-next"
                            disabled={keywordPage === keywordTotalPages}
                            onClick={() => setKeywordPage((prevPage) => Math.min(keywordTotalPages, prevPage + 1))}
                            aria-label="Trang kết quả sau"
                          >
                            Sau →
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}

            {sidebarCarName && !singleSidebarResult && (
              <section className="related-parts-section">
                <h2>Phụ Tùng Khác</h2>
                {searchRelatedLoading && <p>Đang tải phụ tùng liên quan...</p>}
                {!searchRelatedLoading && searchRelatedError && (
                  <p className="error">{searchRelatedError}</p>
                )}
                {!searchRelatedLoading && !searchRelatedError && searchRelatedItems.length === 0 && (
                  <p>No related parts found.</p>
                )}
                {!searchRelatedLoading && !searchRelatedError && searchRelatedItems.length > 0 && (
                  <ul className="related-link-list related-link-card-list">
                    {searchRelatedItems.map((item) => (
                      <li key={item.part_no} className="related-link-item">
                        <Link
                          to={buildPostPathFromItem(item)}
                          state={buildPostStateFromItem(item)}
                          className="related-part-card"
                        >
                          <img
                            src={item.image_url || "/images/Phu_tung_o_to_Hyundai_chinh_hang.webp"}
                            alt={item.alt_text || item.part_name_vn || item.part_no}
                            loading="lazy"
                            decoding="async"
                            className="related-part-image"
                          />
                          <div className="related-part-content">
                            <p className="related-part-priority">Phụ tùng: {item.part_no}</p>
                            <h3>{(item.part_name_vn || item.part_no) + " cho " + (item.car_name || sidebarCarName)}</h3>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function PostDetail() {
  const { partNo, carSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const effectiveCarSlug = carSlug || String(location.state?.carSlug || "").trim();

  React.useEffect(() => {
    if (!partNo || !carSlug) {
      return;
    }

    navigate(buildPostPathFromPartNo(partNo), {
      replace: true,
      state: buildPostStateFromSlug(carSlug),
    });
  }, [carSlug, navigate, partNo]);

  return (
    <main className="home-market-page post-page-layout">
      <div className="home-market-layout post-market-layout">
        <ResponsiveSidebar />

        <section className="home-market-right post-market-right">
          <div className="App-content post-content-shell">
            <PostDetailContent partNo={partNo} carSlug={effectiveCarSlug} />
          </div>
        </section>
      </div>
    </main>
  );
}

function PostDetailContent({
  partNo,
  carSlug = "",
  embedded = false,
  relatedItemsOverride = null,
  relatedLoadingOverride = false,
  relatedErrorOverride = "",
}) {
  const [postData, setPostData] = useState(null);
  const [carEntries, setCarEntries] = useState([]);
  const [carsLoading, setCarsLoading] = useState(false);
  const [relatedItems, setRelatedItems] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState("");
  const [activePostImage, setActivePostImage] = useState(null);
  const [postImageCount, setPostImageCount] = useState(0);
  const [postImageLoading, setPostImageLoading] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const selectedCar = useMemo(() => {
    if (!carSlug) return null;
    return carEntries.find((entry) => entry.slug === carSlug) || null;
  }, [carEntries, carSlug]);

  const displayPartName = useMemo(() => {
    if (!postData) return "Phụ tùng";
    return postData.part_name_vn || postData.part_name || "Phụ tùng";
  }, [postData]);

  const canonicalHeading = useMemo(() => {
    if (!postData) return "Phụ tùng";
    return `${displayPartName} ${postData.part_no}`.trim();
  }, [displayPartName, postData]);

  const postBodyPresentation = useMemo(
    () => extractSeoPostH1ForHeader(postData?.post || ""),
    [postData?.post],
  );

  const headerHeading = postBodyPresentation.headingText || canonicalHeading;

  const compatibleCarGroups = useMemo(() => {
    const deriveModelLabel = (carName) => {
      const normalizedCarName = String(carName || "").replace(/\s+/g, " ").trim();
      if (!normalizedCarName) {
        return "Dòng xe khác";
      }

      const strippedSuffix = normalizedCarName
        .replace(/\s+\d{2,4}:\s.*$/u, "")
        .replace(/\s+\d{2,4}(?:\s.*)?$/u, "")
        .trim();

      return strippedSuffix || normalizedCarName;
    };

    const groups = new Map();

    carEntries.forEach((entry) => {
      const modelLabel = deriveModelLabel(entry.car_name);
      const existingGroup = groups.get(modelLabel);

      if (existingGroup) {
        existingGroup.items.push(entry);
        return;
      }

      groups.set(modelLabel, {
        modelLabel,
        items: [entry],
      });
    });

    return Array.from(groups.values());
  }, [carEntries]);

  const titleText = useMemo(() => {
    if (!postData) return "Phụ tùng";
    if (!carSlug) {
      return canonicalHeading;
    }

    if (!selectedCar) {
      return canonicalHeading;
    }

    return `${canonicalHeading} - ${selectedCar.car_name}`;
  }, [canonicalHeading, postData, selectedCar, carSlug]);

  const fallbackImages = POST_SHARED_IMAGES;
  const usingFetchedPostImages = postImageCount > 0;
  const effectiveImages = useMemo(
    () => (usingFetchedPostImages ? [] : fallbackImages),
    [fallbackImages, usingFetchedPostImages],
  );
  const totalImageCount = usingFetchedPostImages ? postImageCount : effectiveImages.length;
  const activeImage = usingFetchedPostImages
    ? activePostImage
    : effectiveImages[activeImageIndex] || null;

  React.useEffect(() => {
    const loadPost = async () => {
      setLoading(true);
      setError("");
      setCarsLoading(Boolean(partNo));

      try {
        const postRequest = fetch(`/api/posts/${encodeURIComponent(partNo)}`);
        const carsRequest = fetch(`/api/posts/${encodeURIComponent(partNo)}/cars`);

        const postResponse = await postRequest;

        const postPayload = await readJsonSafe(postResponse);

        if (!postResponse.ok) {
          throw new Error(
            postPayload.error ||
              "Không thể tải bài viết. Hãy chạy API bằng lệnh: npm run server",
          );
        }

        setPostData(postPayload);
        setPostImageCount(Number(postPayload.imageCount || 0));
        setActivePostImage(postPayload.primaryImage || null);
        setActiveImageIndex(0);
        setLoading(false);

        try {
          const carsResponse = await carsRequest;
          const carsPayload = carsResponse.ok ? await readJsonSafe(carsResponse) : null;
          setCarEntries(Array.isArray(carsPayload?.cars) ? carsPayload.cars : []);
        } finally {
          setCarsLoading(false);
        }
      } catch (err) {
        setError(err.message || "Lỗi khi tải bài viết");
        setLoading(false);
        setCarsLoading(false);
      }
    };

    if (partNo) {
      loadPost();
    }
  }, [partNo, carSlug]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [partNo, carSlug]);

  React.useEffect(() => {
    if (!embedded && titleText) {
      document.title = titleText;
    }
  }, [embedded, titleText]);

  React.useEffect(() => {
    const loadRelated = async () => {
      if (!partNo || embedded) {
        setRelatedItems([]);
        setRelatedError("");
        return;
      }

      setRelatedLoading(true);
      setRelatedError("");

      try {
        const response = await fetch(
          `/api/posts/${encodeURIComponent(partNo)}/related?limit=8`,
        );
        const payload = await readJsonSafe(response);

        if (!response.ok) {
          throw new Error(
            payload.error ||
              "Không thể tải bài viết liên quan. Hãy chạy API bằng lệnh: npm run server",
          );
        }

        setRelatedItems(Array.isArray(payload.items) ? payload.items : []);
      } catch (err) {
        setRelatedError(err.message || "Lỗi khi tải bài viết liên quan");
        setRelatedItems([]);
      } finally {
        setRelatedLoading(false);
      }
    };

    loadRelated();
  }, [partNo, embedded]);

  React.useEffect(() => {
    setActiveImageIndex(0);
    setPostImageLoading(false);
  }, [partNo, carSlug]);

  React.useEffect(() => {
    if (!usingFetchedPostImages && activeImageIndex >= effectiveImages.length && effectiveImages.length > 0) {
      setActiveImageIndex(0);
    }
  }, [activeImageIndex, effectiveImages.length, usingFetchedPostImages]);

  const fetchPostImageByIndex = React.useCallback(
    async (nextIndex) => {
      if (!partNo || postImageCount <= 0) {
        return;
      }

      setPostImageLoading(true);

      try {
        const response = await fetch(
          `/api/posts/${encodeURIComponent(partNo)}/images?index=${nextIndex}`,
        );
        const payload = await readJsonSafe(response);

        if (!response.ok) {
          throw new Error(
            payload.error || "Không thể tải ảnh phụ tùng. Hãy chạy API bằng lệnh: npm run server",
          );
        }

        setActivePostImage(payload.image || null);
        setPostImageCount(Number(payload.imageCount || 0));
        setActiveImageIndex(Number(payload.index || 0));
      } catch (err) {
        setError(err.message || "Lỗi khi tải ảnh phụ tùng");
      } finally {
        setPostImageLoading(false);
      }
    },
    [partNo, postImageCount],
  );

  const advanceImage = React.useCallback(
    async (step) => {
      if (totalImageCount <= 1) {
        return;
      }

      if (usingFetchedPostImages) {
        const nextIndex = (activeImageIndex + step + totalImageCount) % totalImageCount;
        await fetchPostImageByIndex(nextIndex);
        return;
      }

      setActiveImageIndex((prev) => (prev + step + totalImageCount) % totalImageCount);
    },
    [activeImageIndex, fetchPostImageByIndex, totalImageCount, usingFetchedPostImages],
  );

  const showPrevImage = () => {
    void advanceImage(-1);
  };

  const showNextImage = () => {
    void advanceImage(1);
  };

  const buildRelatedPostPath = (item) => {
    return buildPostPathFromPartNo(item?.part_no || "");
  };

  const usingRelatedOverride = embedded && Array.isArray(relatedItemsOverride);
  const displayedRelatedItems = usingRelatedOverride ? relatedItemsOverride : relatedItems;
  const displayedRelatedLoading = usingRelatedOverride ? relatedLoadingOverride : relatedLoading;
  const displayedRelatedError = usingRelatedOverride ? relatedErrorOverride : relatedError;
  const shouldRenderRelatedSection = !embedded || usingRelatedOverride;

  return (
    <>
      {loading && <p>Đang tải bài viết...</p>}

      {!loading && error && <p className="error">{error}</p>}

      {!loading && !error && postData && carSlug && !selectedCar && !carsLoading && (
        <p className="error">Không tìm thấy bài viết phù hợp với đường dẫn này.</p>
      )}

      {!loading && !error && postData && (!carSlug || selectedCar || carsLoading) && (
        <article className="post-detail-card">
          <header className="post-detail-header">
            <h1>{headerHeading}</h1>
            <div className="post-detail-meta">
              <span>
                <strong>Phụ tùng:</strong> {postData.part_no}
              </span>
              <span>
                <strong>Tên tiếng Anh:</strong> {postData.part_name || "-"}
              </span>
            </div>
          </header>

          <section className="post-media-section" aria-label="Hình ảnh phụ tùng">
            <>
              <button
                type="button"
                className="post-media-nav post-media-nav-prev"
                onClick={showPrevImage}
                aria-label="Xem ảnh trước"
                disabled={totalImageCount <= 1 || postImageLoading}
              >
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="post-media-nav-icon"
                >
                  <path
                    d="M14.5 5.5L8 12l6.5 6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <figure className="post-media-figure">
                <div className="post-media-frame">
                  <img
                    key={activeImage?.src || activeImageIndex}
                    src={activeImage?.src}
                    alt={activeImage?.alt || `Ảnh phụ tùng ${postData.part_no}`}
                    decoding="async"
                    className="post-media-image"
                  />
                </div>
                <figcaption>
                  Ảnh {totalImageCount > 0 ? activeImageIndex + 1 : 0}/{totalImageCount}
                </figcaption>
              </figure>

              <button
                type="button"
                className="post-media-nav post-media-nav-next"
                onClick={showNextImage}
                aria-label="Xem ảnh tiếp theo"
                disabled={totalImageCount <= 1 || postImageLoading}
              >
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="post-media-nav-icon"
                >
                  <path
                    d="M9.5 5.5L16 12l-6.5 6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </>
          </section>

          <div
            className="post-detail-body"
            dangerouslySetInnerHTML={{
              __html:
                stripStyleAttributes(postBodyPresentation.bodyWithoutHeading) ||
                "<p>Chưa có nội dung bài viết.</p>",
            }}
          />

          <section className="post-variant-section">
            <h2 className="post-variant-title">Dòng xe tương thích với {canonicalHeading}</h2>
            {carsLoading && <p>Đang tải danh sách xe tương thích...</p>}
            {!carsLoading && carEntries.length === 0 && (
              <p>Chưa có dữ liệu đời xe tương thích cho phụ tùng này.</p>
            )}
            {!carsLoading && carEntries.length > 0 && (
              <div className="post-variant-groups">
                {compatibleCarGroups.map((group) => (
                  <section key={group.modelLabel} className="post-variant-group">
                    <h3 className="post-variant-group-title">{group.modelLabel}</h3>
                    <ul className="post-variant-list">
                      {group.items.map((entry) => (
                        <li
                          key={entry.slug}
                          className={`post-variant-item ${selectedCar?.slug === entry.slug ? "post-variant-item-active" : ""}`.trim()}
                        >
                          {entry.car_name}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            )}
          </section>

          <section className="post-contact-info" aria-label="Thông tin liên hệ">
            <h2>THÔNG TIN LIÊN HỆ:</h2>
            <ul>
              <li><strong>Hotline:</strong> 0388 861 168</li>
              <li>
                <strong>Facebook:</strong>{" "}
                <a
                  href="https://facebook.com/phutungoto.shop"
                  target="_blank"
                  rel="noreferrer"
                >
                  facebook.com/phutungoto.shop
                </a>
              </li>
              <li>
                <strong>Website:</strong>{" "}
                <a href="https://phutungoto.shop" target="_blank" rel="noreferrer">
                  phutungoto.shop
                </a>
              </li>
              <li>
                <strong>Email:</strong>{" "}
                <a href="mailto:info@phutungoto.shop">info@phutungoto.shop</a>
              </li>
              <li>
                <strong>Văn phòng:</strong> Số 4 - TDP Phượng Nghĩa – Phường Chương Mỹ – Hà Nội
              </li>
            </ul>
          </section>

          {shouldRenderRelatedSection && (
            <section className="related-parts-section">
              <h2>Phụ Tùng Khác</h2>

              {displayedRelatedLoading && <p>Loading related parts...</p>}

              {!displayedRelatedLoading && displayedRelatedError && (
                <p className="error">{displayedRelatedError}</p>
              )}

              {!displayedRelatedLoading && !displayedRelatedError && displayedRelatedItems.length === 0 && (
                <p>No related parts found.</p>
              )}

              {!displayedRelatedLoading && !displayedRelatedError && displayedRelatedItems.length > 0 && (
                <ul className="related-link-list related-link-card-list">
                  {displayedRelatedItems.map((item) => (
                    <li key={item.part_no} className="related-link-item">
                      <Link
                        to={buildRelatedPostPath(item)}
                        state={buildPostStateFromItem(item)}
                        className="related-part-card"
                      >
                        <img
                          src={item.image_url || "/images/Phu_tung_o_to_Hyundai_chinh_hang.webp"}
                          alt={item.alt_text || item.title || item.part_name_vn || item.part_name || item.part_no}
                          loading="lazy"
                          decoding="async"
                          className="related-part-image"
                        />
                        <div className="related-part-content">
                          <p className="related-part-priority">Phụ tùng: {item.part_no}</p>
                          <h3>
                            {(item.part_name_vn || item.part_name || "Bài viết") +
                              " cho " +
                              (item.car_name || "xe")}
                          </h3>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </article>
      )}
    </>
  );
}

function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      setError("Vui lòng nhập đầy đủ tên đăng nhập và mật khẩu.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/admin/login", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      });

      const payload = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(payload.error || "Đăng nhập admin thất bại.");
      }

      navigate("/admin/orders", { replace: true });
    } catch (submitError) {
      setError(submitError.message || "Không thể đăng nhập admin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="App-content admin-login-page">
      <section className="admin-login-card">
        <p className="admin-login-kicker">Khu vực quản trị</p>
        <h1>Đăng nhập Admin</h1>
        <p className="admin-login-description">
          Nhập tài khoản quản trị để lấy JWT và sử dụng các route /admin đã được bảo vệ.
        </p>

        <form className="admin-login-form" onSubmit={handleSubmit} noValidate>
          <label>
            Tên đăng nhập
            <input
              type="text"
              name="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              placeholder="admin"
            />
          </label>

          <label>
            Mật khẩu
            <input
              type="password"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="Nhap mat khau"
            />
          </label>

          {error && <p className="error">{error}</p>}

          <button type="submit" className="btn-primary admin-login-button" disabled={loading}>
            {loading ? "Dang dang nhap..." : "Dang nhap"}
          </button>
        </form>
      </section>
    </main>
  );
}

function MechanicLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!username.trim() || !password) {
      setError("Vui lòng nhập đầy đủ tài khoản và mật khẩu của thợ.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/mechanic/login", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      });
      const payload = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(payload.error || "Đăng nhập thợ thất bại.");
      }

      navigate("/mechanic/uploads", { replace: true });
    } catch (submitError) {
      setError(submitError.message || "Không thể đăng nhập thợ.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="App-content admin-login-page">
      <section className="admin-login-card mechanic-login-card">
        <p className="admin-login-kicker">Khu vực thợ sửa chữa</p>
        <h1>Đăng nhập Upload Media</h1>
        <p className="admin-login-description">
          Đăng nhập để tải ảnh hoặc video thực tế của phụ tùng lên kho S3 nội bộ.
        </p>

        <form className="admin-login-form" onSubmit={handleSubmit} noValidate>
          <label>
            Tên đăng nhập
            <input
              type="text"
              name="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              placeholder="mechanic"
            />
          </label>

          <label>
            Mật khẩu
            <input
              type="password"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="Nhập mật khẩu"
            />
          </label>

          {error && <p className="error">{error}</p>}

          <button type="submit" className="btn-primary admin-login-button" disabled={loading}>
            {loading ? "Đang đăng nhập..." : "Đăng nhập"}
          </button>
        </form>
      </section>
    </main>
  );
}

function MechanicUploads() {
  const navigate = useNavigate();
  const previewUrlsRef = useRef(new Map());
  const [mechanicName, setMechanicName] = useState("");
  const [partNo, setPartNo] = useState("");
  const [vehicleContext, setVehicleContext] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let ignore = false;

    const loadSession = async () => {
      try {
        const response = await fetch("/mechanic/session", {
          credentials: "include",
        });
        const payload = await readJsonSafe(response);

        if (response.status === 401 || response.status === 403) {
          navigate("/mechanic/login", { replace: true });
          return;
        }

        if (!response.ok) {
          throw new Error(payload.error || "Không thể xác thực phiên thợ.");
        }

        if (!ignore) {
          setMechanicName(payload.mechanic?.username || "mechanic");
        }
      } catch (sessionError) {
        if (!ignore) {
          setError(sessionError.message || "Không thể tải phiên thợ.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadSession();

    return () => {
      ignore = true;
    };
  }, [navigate]);

  useEffect(() => {
    const previewUrls = previewUrlsRef.current;

    return () => {
      for (const previewUrl of previewUrls.values()) {
        URL.revokeObjectURL(previewUrl);
      }
      previewUrls.clear();
    };
  }, []);

  const handleLogout = async () => {
    try {
      await fetch("/mechanic/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (_) {
      // Ignore logout errors and continue clearing the UI session.
    } finally {
      navigate("/mechanic/login", { replace: true });
    }
  };

  const handleAddFiles = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";

    if (selectedFiles.length === 0) {
      return;
    }

    setError("");
    setSuccess("");

    setFiles((previousFiles) => {
      const availableSlots = Math.max(0, MAX_MECHANIC_UPLOAD_FILES - previousFiles.length);
      const nextFiles = selectedFiles.slice(0, availableSlots).map((file) => {
        const id =
          typeof window.crypto?.randomUUID === "function"
            ? window.crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`;
        const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : "";
        if (previewUrl) {
          previewUrlsRef.current.set(id, previewUrl);
        }

        return {
          id,
          file,
          description: "",
          previewUrl,
          status: "ready",
          error: "",
          uploadedUrl: "",
        };
      });

      return [...previousFiles, ...nextFiles];
    });
  };

  const handleDescriptionChange = (id, description) => {
    setFiles((previousFiles) =>
      previousFiles.map((entry) =>
        entry.id === id ? { ...entry, description, error: "" } : entry,
      ),
    );
  };

  const handleRemoveFile = (id) => {
    const previewUrl = previewUrlsRef.current.get(id);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      previewUrlsRef.current.delete(id);
    }

    setFiles((previousFiles) => previousFiles.filter((entry) => entry.id !== id));
  };

  const updateFileState = (id, updater) => {
    setFiles((previousFiles) =>
      previousFiles.map((entry) =>
        entry.id === id ? { ...entry, ...updater } : entry,
      ),
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const normalizedPartNo = partNo.trim();
    if (!normalizedPartNo) {
      setError("Vui lòng nhập mã phụ tùng trước khi tải media.");
      return;
    }

    if (files.length === 0) {
      setError("Vui lòng chọn ít nhất một ảnh hoặc video.");
      return;
    }

    setSubmitting(true);
    let completedCount = 0;

    for (const entry of files) {
      try {
        updateFileState(entry.id, { status: "uploading", error: "" });

        const presignResponse = await fetch("/mechanic/media/presign", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            partNo: normalizedPartNo,
            fileName: entry.file.name,
            contentType: entry.file.type,
          }),
        });
        const presignPayload = await readJsonSafe(presignResponse);

        if (presignResponse.status === 401 || presignResponse.status === 403) {
          navigate("/mechanic/login", { replace: true });
          return;
        }

        if (!presignResponse.ok) {
          throw new Error(presignPayload.error || "Không thể xin URL upload S3.");
        }

        const uploadResponse = await fetch(presignPayload.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": entry.file.type,
          },
          body: entry.file,
        });

        if (!uploadResponse.ok) {
          throw new Error("Upload file lên S3 thất bại.");
        }

        const saveResponse = await fetch("/mechanic/media", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            partNo: normalizedPartNo,
            objectKey: presignPayload.objectKey,
            publicUrl: presignPayload.publicUrl,
            contentType: entry.file.type,
            fileName: entry.file.name,
            description: entry.description,
            vehicleContext,
          }),
        });
        const savePayload = await readJsonSafe(saveResponse);

        if (!saveResponse.ok) {
          throw new Error(savePayload.error || "Không thể lưu metadata media.");
        }

        completedCount += 1;
        updateFileState(entry.id, {
          status: "done",
          uploadedUrl: savePayload.media?.public_url || presignPayload.publicUrl,
        });
      } catch (submitError) {
        updateFileState(entry.id, {
          status: "error",
          error: submitError.message || "Upload thất bại.",
        });
      }
    }

    setSubmitting(false);
    if (completedCount > 0) {
      setSuccess(`Đã tải thành công ${completedCount}/${files.length} file. Admin có thể xem danh sách media chờ duyệt ngay bây giờ.`);
    }
  };

  return (
    <main className="App-content admin-orders-page">
      <section className="admin-orders-card mechanic-upload-card">
        <header className="admin-orders-header mechanic-upload-header">
          <div>
            <p className="admin-login-kicker">Khu vực thợ sửa chữa</p>
            <h1>Tải Ảnh Và Video Thực Tế</h1>
            <p className="mechanic-upload-subtitle">
              Tài khoản: <strong>{mechanicName || "mechanic"}</strong>
            </p>
          </div>
          <div className="admin-header-actions">
            <button type="button" className="admin-logout-button" onClick={handleLogout}>
              Đăng xuất
            </button>
          </div>
        </header>

        {loading && <p className="admin-orders-status">Đang kiểm tra phiên thợ...</p>}
        {!loading && error && <p className="admin-orders-error">{error}</p>}
        {!loading && success && <p className="mechanic-upload-success">{success}</p>}

        {!loading && (
          <form className="mechanic-upload-form" onSubmit={handleSubmit}>
            <div className="mechanic-upload-grid">
              <label className="admin-modal-label">
                Mã phụ tùng *
                <input
                  className="admin-modal-input"
                  value={partNo}
                  onChange={(event) => setPartNo(event.target.value)}
                  placeholder="Ví dụ: 2630002503"
                />
              </label>

              <label className="admin-modal-label">
                Bối cảnh xe / ghi chú chung
                <input
                  className="admin-modal-input"
                  value={vehicleContext}
                  onChange={(event) => setVehicleContext(event.target.value)}
                  placeholder="Ví dụ: ACCENT 15 - thay bảo dưỡng 5.000km"
                />
              </label>
            </div>

            <label className="mechanic-upload-picker">
              <span>Chọn tối đa {MAX_MECHANIC_UPLOAD_FILES} file ảnh hoặc video *</span>
              <input
                type="file"
                multiple
                accept="image/*,video/mp4,video/quicktime,video/webm"
                onChange={handleAddFiles}
                disabled={submitting || files.length >= MAX_MECHANIC_UPLOAD_FILES}
              />
            </label>

            {files.length === 0 && (
              <p className="admin-orders-status">Chưa có file nào được chọn.</p>
            )}

            {files.length > 0 && (
              <div className="mechanic-upload-list">
                {files.map((entry, index) => (
                  <article key={entry.id} className="mechanic-upload-item">
                    <div className="mechanic-upload-preview">
                      {entry.previewUrl ? (
                        <img
                          src={entry.previewUrl}
                          alt={entry.file.name}
                          className="mechanic-upload-preview-image"
                        />
                      ) : (
                        <div className="mechanic-upload-video-placeholder">
                          <strong>Video</strong>
                          <span>{entry.file.name}</span>
                        </div>
                      )}
                    </div>

                    <div className="mechanic-upload-item-body">
                      <div className="mechanic-upload-item-head">
                        <strong>File {index + 1}</strong>
                        <span>{entry.file.name}</span>
                      </div>

                      <label className="admin-modal-label">
                        Mô tả file này
                        <textarea
                          className="admin-modal-textarea mechanic-upload-description"
                          rows={4}
                          value={entry.description}
                          onChange={(event) => handleDescriptionChange(entry.id, event.target.value)}
                          placeholder="Ví dụ: Ảnh cận cảnh tem phụ tùng, ảnh thực tế sau khi tháo ra, vị trí lắp trên xe..."
                          disabled={submitting}
                        />
                      </label>

                      <div className="mechanic-upload-meta">
                        <span className={`mechanic-upload-status mechanic-upload-status-${entry.status}`}>
                          {entry.status === "ready" && "Sẵn sàng upload"}
                          {entry.status === "uploading" && "Đang upload"}
                          {entry.status === "done" && "Đã ghi nhận"}
                          {entry.status === "error" && "Lỗi upload"}
                        </span>
                        {entry.error ? <span className="admin-orders-error">{entry.error}</span> : null}
                        {entry.uploadedUrl ? (
                          <a href={entry.uploadedUrl} target="_blank" rel="noreferrer" className="mechanic-upload-link">
                            Mở file đã tải lên
                          </a>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        className="admin-delete-button"
                        onClick={() => handleRemoveFile(entry.id)}
                        disabled={submitting}
                      >
                        Xóa file
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}

            <div className="admin-modal-actions">
              <button type="submit" className="btn-primary" disabled={submitting || loading}>
                {submitting ? "Đang upload lên S3..." : "Tải media lên"}
              </button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}

function AdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const loadOrders = useCallback(async () => {
    setError("");

    try {
      const response = await fetch("/admin/orders", {
        method: "GET",
        credentials: "include",
      });

      const payload = await readJsonSafe(response);

      if (response.status === 401 || response.status === 403) {
        navigate("/admin/login", { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error(payload.error || "Không thể tải danh sách đơn hàng.");
      }

      setOrders(Array.isArray(payload.orders) ? payload.orders : []);
    } catch (fetchError) {
      setError(fetchError.message || "Lỗi khi tải đơn hàng admin.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleLogout = async () => {
    try {
      await fetch("/admin/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch (logoutError) {
      console.error("Admin logout error:", logoutError);
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const confirmed = window.confirm(`Xac nhan xoa don hang #${orderId}?`);
    if (!confirmed) {
      return;
    }

    setDeletingId(orderId);
    setError("");

    try {
      const response = await fetch(`/admin/orders/${orderId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const payload = await readJsonSafe(response);

      if (response.status === 401 || response.status === 403) {
        navigate("/admin/login", { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error(payload.error || "Khong the xoa don hang.");
      }

      setOrders((prev) => prev.filter((order) => order.id !== orderId));
    } catch (deleteError) {
      setError(deleteError.message || "Loi khi xoa don hang.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <main className="App-content admin-orders-page">
      <section className="admin-orders-card">
        <header className="admin-orders-header">
          <div>
            <p className="admin-login-kicker">Khu vực quản trị</p>
            <h1>Danh sách đơn hàng</h1>
          </div>
          <div className="admin-header-actions">
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/mechanics")}>Account thợ</button>
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/posts")}>Bài viết</button>
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/mechanic-media")}>Media thợ</button>
            <button type="button" className="admin-logout-button" onClick={handleLogout}>
              Đăng xuất
            </button>
          </div>
        </header>

        {loading && <p className="admin-orders-status">Đang tải đơn hàng...</p>}

        {!loading && error && <p className="admin-orders-error">{error}</p>}

        {!loading && !error && orders.length === 0 && (
          <p className="admin-orders-status">Chưa có đơn hàng nào.</p>
        )}

        {!loading && !error && orders.length > 0 && (
          <div className="admin-orders-table-wrap">
            <table className="admin-orders-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Khách hàng</th>
                  <th>Mã phụ tùng</th>
                  <th>Tên phụ tùng</th>
                  <th>Số lượng</th>
                  <th>Liên hệ</th>
                  <th>Thời gian</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>{order.id}</td>
                    <td>{order.name || "-"}</td>
                    <td>{order.part_no || "-"}</td>
                    <td>{order.part_name_vn || order.product || "-"}</td>
                    <td>{order.quantity || "-"}</td>
                    <td>
                      <div>{order.phone || "-"}</div>
                      <div>{order.email || "-"}</div>
                    </td>
                    <td>{order.created_at ? new Date(order.created_at).toLocaleString() : "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-delete-button"
                        onClick={() => handleDeleteOrder(order.id)}
                        disabled={deletingId === order.id}
                      >
                        {deletingId === order.id ? "Đang xóa..." : "Xóa"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function AdminPosts() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [offset, setOffset] = useState(0);

  // State cho modal chỉnh sửa
  const [editPost, setEditPost] = useState(null); // { part_no, part_name, part_name_vn, post }
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [editShowSource, setEditShowSource] = useState(false);

  // State cho modal tạo mới
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ part_no: "", part_name: "", part_name_vn: "", post: "" });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createShowSource, setCreateShowSource] = useState(false);

  const [deletingPartNo, setDeletingPartNo] = useState(null);

  const loadPosts = useCallback(async (s, off) => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ limit: ADMIN_POSTS_LIMIT, offset: off });
        if (s) params.set("search", s);
        const res = await fetch(`/admin/posts?${params}`, { credentials: "include" });
        const data = await readJsonSafe(res);
        if (res.status === 401 || res.status === 403) { navigate("/admin/login", { replace: true }); return; }
        if (!res.ok) throw new Error(getApiErrorMessage(data, "Không thể tải danh sách bài viết."));
        setPosts(data.posts || []);
        setTotal(data.total || 0);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
  }, [navigate]);

  useEffect(() => { loadPosts(search, offset); }, [loadPosts, search, offset]);

  const handleSearch = (e) => {
      e.preventDefault();
      setOffset(0);
      setSearch(searchInput.trim());
  };

  const handleLogout = async () => {
      try { await fetch("/admin/logout", { method: "POST", credentials: "include" }); } catch (_) {}
      navigate("/admin/login", { replace: true });
  };

    // Mở modal edit: fetch nội dung đầy đủ
  const handleEdit = async (partNo) => {
      setEditError("");
      setEditPost(null);
      try {
        const res = await fetch(`/admin/posts/${encodeURIComponent(partNo)}`, { credentials: "include" });
        const data = await readJsonSafe(res);
        if (res.status === 401 || res.status === 403) { navigate("/admin/login", { replace: true }); return; }
        if (!res.ok) throw new Error(getApiErrorMessage(data, "Không thể tải bài viết."));
        setEditShowSource(false);
        setEditPost(data);
      } catch (e) {
        setError(e.message);
      }
  };

  const handleEditSave = async () => {
      setEditLoading(true);
      setEditError("");
      try {
        const res = await fetch(`/admin/posts/${encodeURIComponent(editPost.part_no)}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ part_name: editPost.part_name, part_name_vn: editPost.part_name_vn, post: editPost.post }),
        });
        const data = await readJsonSafe(res);
        if (!res.ok) throw new Error(getApiErrorMessage(data, "Không thể lưu bài viết."));
        setEditPost(null);
        loadPosts(search, offset);
      } catch (e) {
        setEditError(e.message);
      } finally {
        setEditLoading(false);
      }
  };

  const handleDelete = async (partNo) => {
      if (!window.confirm(`Xác nhận xóa bài viết "${partNo}"?`)) return;
      setDeletingPartNo(partNo);
      try {
        const res = await fetch(`/admin/posts/${encodeURIComponent(partNo)}`, { method: "DELETE", credentials: "include" });
        const data = await readJsonSafe(res);
        if (res.status === 401 || res.status === 403) { navigate("/admin/login", { replace: true }); return; }
        if (!res.ok) throw new Error(getApiErrorMessage(data, "Không thể xóa bài viết."));
        setPosts((prev) => prev.filter((p) => p.part_no !== partNo));
        setTotal((t) => t - 1);
      } catch (e) {
        setError(e.message);
      } finally {
        setDeletingPartNo(null);
      }
  };

  const handleCreateSave = async () => {
      setCreateLoading(true);
      setCreateError("");
      try {
        const res = await fetch("/admin/posts", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(createForm),
        });
        const data = await readJsonSafe(res);
        if (!res.ok) throw new Error(getApiErrorMessage(data, "Không thể tạo bài viết."));
        setShowCreate(false);
        setCreateShowSource(false);
        setCreateForm({ part_no: "", part_name: "", part_name_vn: "", post: "" });
        loadPosts(search, offset);
      } catch (e) {
        setCreateError(e.message);
      } finally {
        setCreateLoading(false);
      }
  };

  return (
      <main className="App-content admin-orders-page">
        <section className="admin-orders-card">
          <header className="admin-orders-header">
            <div>
              <p className="admin-login-kicker">Khu vực quản trị</p>
              <h1>Quản lý bài viết <span className="admin-posts-total">({total})</span></h1>
            </div>
            <div className="admin-header-actions">
              <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/orders")}>← Đơn hàng</button>
              <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/mechanics")}>Account thợ</button>
              <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/mechanic-media")}>Media thợ</button>
              <button
                type="button"
                className="admin-create-button"
                onClick={() => {
                  setShowCreate(true);
                  setCreateError("");
                  setCreateShowSource(false);
                }}
              >
                + Tạo bài viết
              </button>
              <button type="button" className="admin-logout-button" onClick={handleLogout}>Đăng xuất</button>
            </div>
          </header>

          {/* Search */}
          <form className="admin-posts-search-form" onSubmit={handleSearch}>
            <input
              type="text"
              className="admin-posts-search-input"
              placeholder="Tìm theo mã, tên phụ tùng..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn-primary">Tìm</button>
            {search && (
              <button type="button" className="admin-logout-button" onClick={() => { setSearchInput(""); setSearch(""); setOffset(0); }}>Xóa lọc</button>
            )}
          </form>

          {loading && <p className="admin-orders-status">Đang tải...</p>}
          {!loading && error && <p className="admin-orders-error">{error}</p>}
          {!loading && !error && posts.length === 0 && <p className="admin-orders-status">Không có bài viết nào.</p>}

          {!loading && !error && posts.length > 0 && (
            <>
              <div className="admin-orders-table-wrap">
                <table className="admin-orders-table">
                  <thead>
                    <tr>
                      <th>Mã phụ tùng</th>
                      <th>Tên (EN)</th>
                      <th>Tên (VN)</th>
                      <th>Xe áp dụng</th>
                      <th>Xem trước</th>
                      <th>Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((p) => (
                      <tr key={p.part_no}>
                        <td><code>{p.part_no}</code></td>
                        <td>{p.part_name || "-"}</td>
                        <td>{p.part_name_vn || "-"}</td>
                        <td>{p.total_count != null ? p.total_count : "-"}</td>
                        <td className="admin-post-preview">{p.post_preview ? p.post_preview.slice(0, 80) + "…" : "-"}</td>
                        <td>
                          <button type="button" className="admin-edit-button" onClick={() => handleEdit(p.part_no)}>Sửa</button>
                          <button type="button" className="admin-delete-button" onClick={() => handleDelete(p.part_no)} disabled={deletingPartNo === p.part_no}>
                            {deletingPartNo === p.part_no ? "Đang xóa..." : "Xóa"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Phân trang */}
              <div className="admin-posts-pagination">
                <button type="button" className="admin-logout-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - ADMIN_POSTS_LIMIT))}>← Trang trước</button>
                <span>{offset + 1}–{Math.min(offset + ADMIN_POSTS_LIMIT, total)} / {total}</span>
                <button type="button" className="admin-logout-button" disabled={offset + ADMIN_POSTS_LIMIT >= total} onClick={() => setOffset(offset + ADMIN_POSTS_LIMIT)}>Trang sau →</button>
              </div>
            </>
          )}
        </section>

        {/* Modal chỉnh sửa bài viết */}
        {editPost && (
          <div className="admin-modal-overlay" onClick={() => setEditPost(null)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Chỉnh sửa bài viết: <code>{editPost.part_no}</code></h2>
              <label className="admin-modal-label">Tên EN
                <input className="admin-modal-input" value={editPost.part_name || ""} onChange={(e) => setEditPost({ ...editPost, part_name: e.target.value })} />
              </label>
              <label className="admin-modal-label">Tên VN
                <input className="admin-modal-input" value={editPost.part_name_vn || ""} onChange={(e) => setEditPost({ ...editPost, part_name_vn: e.target.value })} />
              </label>
              <label className="admin-modal-label">Nội dung bài viết</label>
              <RichPostEditor
                value={editPost.post || ""}
                onChange={(html) => setEditPost({ ...editPost, post: html })}
                placeholder="Bắt đầu viết nội dung bài viết..."
              />
              <button
                type="button"
                className="admin-source-toggle"
                onClick={() => setEditShowSource((prev) => !prev)}
              >
                {editShowSource ? "Ẩn HTML thô" : "Hiện HTML thô"}
              </button>
              {editShowSource && (
                <textarea
                  className="admin-modal-textarea"
                  rows={8}
                  value={editPost.post || ""}
                  onChange={(e) => setEditPost({ ...editPost, post: e.target.value })}
                />
              )}
              {editError && <p className="admin-orders-error">{editError}</p>}
              <div className="admin-modal-actions">
                <button type="button" className="btn-primary" onClick={handleEditSave} disabled={editLoading}>{editLoading ? "Đang lưu..." : "Lưu"}</button>
                <button type="button" className="admin-logout-button" onClick={() => setEditPost(null)}>Hủy</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal tạo bài viết mới */}
        {showCreate && (
          <div className="admin-modal-overlay" onClick={() => setShowCreate(false)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Tạo bài viết mới</h2>
              <label className="admin-modal-label">Mã phụ tùng *
                <input className="admin-modal-input" value={createForm.part_no} onChange={(e) => setCreateForm({ ...createForm, part_no: e.target.value })} />
              </label>
              <label className="admin-modal-label">Tên EN
                <input className="admin-modal-input" value={createForm.part_name} onChange={(e) => setCreateForm({ ...createForm, part_name: e.target.value })} />
              </label>
              <label className="admin-modal-label">Tên VN *
                <input className="admin-modal-input" value={createForm.part_name_vn} onChange={(e) => setCreateForm({ ...createForm, part_name_vn: e.target.value })} />
              </label>
              <label className="admin-modal-label">Nội dung bài viết *</label>
              <RichPostEditor
                value={createForm.post}
                onChange={(html) => setCreateForm({ ...createForm, post: html })}
                placeholder="Viết nội dung theo phong cách Notion/Medium..."
              />
              <button
                type="button"
                className="admin-source-toggle"
                onClick={() => setCreateShowSource((prev) => !prev)}
              >
                {createShowSource ? "Ẩn HTML thô" : "Hiện HTML thô"}
              </button>
              {createShowSource && (
                <textarea
                  className="admin-modal-textarea"
                  rows={8}
                  value={createForm.post}
                  onChange={(e) => setCreateForm({ ...createForm, post: e.target.value })}
                />
              )}
              {createError && <p className="admin-orders-error">{createError}</p>}
              <div className="admin-modal-actions">
                <button type="button" className="btn-primary" onClick={handleCreateSave} disabled={createLoading}>{createLoading ? "Đang tạo..." : "Tạo"}</button>
                <button type="button" className="admin-logout-button" onClick={() => setShowCreate(false)}>Hủy</button>
              </div>
            </div>
          </div>
        )}
        </main>
      );
}

function AdminMechanicMedia() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [offset, setOffset] = useState(0);

  const loadItems = useCallback(async (nextSearch, nextStatus, nextOffset) => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        limit: String(MECHANIC_MEDIA_PAGE_LIMIT),
        offset: String(nextOffset),
        status: nextStatus,
      });
      if (nextSearch) {
        params.set("search", nextSearch);
      }

      const response = await fetch(`/admin/mechanic-media?${params.toString()}`, {
        credentials: "include",
      });
      const payload = await readJsonSafe(response);

      if (response.status === 401 || response.status === 403) {
        navigate("/admin/login", { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error(payload.error || "Không thể tải media chờ duyệt.");
      }

      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(Number(payload.total || 0));
    } catch (fetchError) {
      setError(fetchError.message || "Không thể tải media chờ duyệt.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadItems(search, statusFilter, offset);
  }, [loadItems, offset, search, statusFilter]);

  const handleLogout = async () => {
    try {
      await fetch("/admin/logout", { method: "POST", credentials: "include" });
    } catch (_) {
      // Ignore logout errors.
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setOffset(0);
    setSearch(searchInput.trim());
  };

  return (
    <main className="App-content admin-orders-page">
      <section className="admin-orders-card">
        <header className="admin-orders-header">
          <div>
            <p className="admin-login-kicker">Khu vực quản trị</p>
            <h1>Media Thợ Chờ Duyệt <span className="admin-posts-total">({total})</span></h1>
          </div>
          <div className="admin-header-actions">
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/orders")}>← Đơn hàng</button>
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/posts")}>Bài viết</button>
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/mechanics")}>Account thợ</button>
            <button type="button" className="admin-logout-button" onClick={handleLogout}>Đăng xuất</button>
          </div>
        </header>

        <form className="admin-posts-search-form" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            className="admin-posts-search-input"
            placeholder="Tìm theo mã phụ tùng, mô tả hoặc bối cảnh xe..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <select
            className="admin-modal-input mechanic-status-filter"
            value={statusFilter}
            onChange={(event) => {
              setOffset(0);
              setStatusFilter(event.target.value);
            }}
          >
            <option value="pending">Chờ duyệt</option>
            <option value="approved">Đã duyệt</option>
            <option value="rejected">Từ chối</option>
            <option value="all">Tất cả</option>
          </select>
          <button type="submit" className="btn-primary">Tìm</button>
        </form>

        {loading && <p className="admin-orders-status">Đang tải media...</p>}
        {!loading && error && <p className="admin-orders-error">{error}</p>}
        {!loading && !error && items.length === 0 && (
          <p className="admin-orders-status">Không có media nào cho bộ lọc hiện tại.</p>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="admin-orders-table-wrap">
              <table className="admin-orders-table mechanic-media-table">
                <thead>
                  <tr>
                    <th>Preview</th>
                    <th>Mã phụ tùng</th>
                    <th>Loại</th>
                    <th>Mô tả</th>
                    <th>Bối cảnh xe</th>
                    <th>Người tải</th>
                    <th>Thời gian</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.media_type === "image" ? (
                          <a href={item.public_url} target="_blank" rel="noreferrer" className="mechanic-admin-preview-link">
                            <img src={item.public_url} alt={item.description || item.part_no} className="mechanic-admin-preview-image" />
                          </a>
                        ) : (
                          <a href={item.public_url} target="_blank" rel="noreferrer" className="mechanic-upload-link">
                            Xem video
                          </a>
                        )}
                      </td>
                      <td><code>{item.part_no}</code></td>
                      <td>{item.media_type}</td>
                      <td>{item.description || "-"}</td>
                      <td>{item.vehicle_context || "-"}</td>
                      <td>{item.uploaded_by || "-"}</td>
                      <td>{item.created_at ? new Date(item.created_at).toLocaleString() : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="admin-posts-pagination">
              <button type="button" className="admin-logout-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - MECHANIC_MEDIA_PAGE_LIMIT))}>← Trang trước</button>
              <span>{offset + 1}–{Math.min(offset + MECHANIC_MEDIA_PAGE_LIMIT, total)} / {total}</span>
              <button type="button" className="admin-logout-button" disabled={offset + MECHANIC_MEDIA_PAGE_LIMIT >= total} onClick={() => setOffset(offset + MECHANIC_MEDIA_PAGE_LIMIT)}>Trang sau →</button>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

function AdminMechanics() {
  const navigate = useNavigate();
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [resettingId, setResettingId] = useState(null);
  const [createForm, setCreateForm] = useState({ username: "", password: "", displayName: "" });
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({ password: "", confirmPassword: "" });
  const [resetPasswordError, setResetPasswordError] = useState("");

  const loadMechanics = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/admin/mechanics", {
        credentials: "include",
      });
      const payload = await readJsonSafe(response);

      if (response.status === 401 || response.status === 403) {
        navigate("/admin/login", { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error(payload.error || "Không thể tải danh sách tài khoản người dùng.");
      }

      setMechanics(Array.isArray(payload.mechanics) ? payload.mechanics : []);
    } catch (fetchError) {
      setError(fetchError.message || "Không thể tải danh sách tài khoản người dùng.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadMechanics();
  }, [loadMechanics]);

  const handleLogout = async () => {
    try {
      await fetch("/admin/logout", { method: "POST", credentials: "include" });
    } catch (_) {
      // Ignore logout errors.
    } finally {
      navigate("/admin/login", { replace: true });
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setError("");

    if (!createForm.username.trim() || !createForm.password) {
      setError("Vui lòng nhập tên đăng nhập và mật khẩu cho tài khoản người dùng.");
      return;
    }

    setCreating(true);

    try {
      const response = await fetch("/admin/mechanics", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: createForm.username.trim(),
          password: createForm.password,
          displayName: createForm.displayName.trim(),
        }),
      });
      const payload = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(payload.error || "Không thể tạo tài khoản người dùng.");
      }

      setCreateForm({ username: "", password: "", displayName: "" });
      setMechanics((previous) => [payload.mechanic, ...previous]);
    } catch (submitError) {
      setError(submitError.message || "Không thể tạo tài khoản người dùng.");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleStatus = async (mechanic) => {
    const nextIsActive = !mechanic.is_active;
    const confirmed = window.confirm(
      nextIsActive
        ? `Bật lại tài khoản người dùng "${mechanic.username}"?`
        : `Vô hiệu hóa tài khoản người dùng "${mechanic.username}"?`,
    );

    if (!confirmed) {
      return;
    }

    setTogglingId(mechanic.id);
    setError("");

    try {
      const response = await fetch(`/admin/mechanics/${mechanic.id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isActive: nextIsActive }),
      });
      const payload = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(payload.error || "Không thể cập nhật trạng thái tài khoản người dùng.");
      }

      setMechanics((previous) =>
        previous.map((entry) =>
          entry.id === mechanic.id ? { ...entry, ...payload.mechanic } : entry,
        ),
      );
    } catch (toggleError) {
      setError(toggleError.message || "Không thể cập nhật trạng thái tài khoản người dùng.");
    } finally {
      setTogglingId(null);
    }
  };

  const openResetPasswordModal = (mechanic) => {
    setResetPasswordModal(mechanic);
    setResetPasswordForm({ password: "", confirmPassword: "" });
    setResetPasswordError("");
  };

  const closeResetPasswordModal = () => {
    if (resettingId) {
      return;
    }

    setResetPasswordModal(null);
    setResetPasswordForm({ password: "", confirmPassword: "" });
    setResetPasswordError("");
  };

  const handleResetPassword = async () => {
    if (!resetPasswordModal) {
      return;
    }

    if (!resetPasswordForm.password) {
      setResetPasswordError("Mật khẩu mới không được để trống.");
      return;
    }

    if (resetPasswordForm.password !== resetPasswordForm.confirmPassword) {
      setResetPasswordError("Mật khẩu nhập lại không khớp.");
      return;
    }

    setResettingId(resetPasswordModal.id);
    setError("");
    setResetPasswordError("");

    try {
      const response = await fetch(`/admin/mechanics/${resetPasswordModal.id}/password`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: resetPasswordForm.password }),
      });
      const payload = await readJsonSafe(response);

      if (!response.ok) {
        throw new Error(payload.error || "Không thể reset mật khẩu tài khoản người dùng.");
      }

      setMechanics((previous) =>
        previous.map((entry) =>
          entry.id === resetPasswordModal.id ? { ...entry, ...payload.mechanic } : entry,
        ),
      );
      closeResetPasswordModal();
    } catch (resetError) {
      setResetPasswordError(resetError.message || "Không thể reset mật khẩu tài khoản người dùng.");
    } finally {
      setResettingId(null);
    }
  };

  return (
    <main className="App-content admin-orders-page">
      <section className="admin-orders-card">
        <header className="admin-orders-header">
          <div>
            <p className="admin-login-kicker">Khu vực quản trị</p>
            <h1>Tài khoản người dùng <span className="admin-posts-total">({mechanics.length})</span></h1>
          </div>
          <div className="admin-header-actions">
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/orders")}>← Đơn hàng</button>
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/posts")}>Bài viết</button>
            <button type="button" className="admin-nav-link" onClick={() => navigate("/admin/mechanic-media")}>Media</button>
            <button type="button" className="admin-logout-button" onClick={handleLogout}>Đăng xuất</button>
          </div>
        </header>

        <form className="admin-posts-search-form admin-mechanic-form" onSubmit={handleCreate}>
          <input
            type="text"
            className="admin-posts-search-input"
            placeholder="Tên đăng nhập"
            value={createForm.username}
            onChange={(event) => setCreateForm((previous) => ({ ...previous, username: event.target.value }))}
          />
          <input
            type="text"
            className="admin-posts-search-input"
            placeholder="Tên hiển thị"
            value={createForm.displayName}
            onChange={(event) => setCreateForm((previous) => ({ ...previous, displayName: event.target.value }))}
          />
          <input
            type="password"
            className="admin-posts-search-input"
            placeholder="Mật khẩu ban đầu"
            value={createForm.password}
            onChange={(event) => setCreateForm((previous) => ({ ...previous, password: event.target.value }))}
          />
          <button type="submit" className="btn-primary" disabled={creating}>
            {creating ? "Đang tạo..." : "Tạo account"}
          </button>
        </form>

        {loading && <p className="admin-orders-status">Đang tải danh sách tài khoản người dùng...</p>}
        {!loading && error && <p className="admin-orders-error">{error}</p>}
        {!loading && !error && mechanics.length === 0 && (
          <p className="admin-orders-status">Chưa có tài khoản người dùng nào trong database.</p>
        )}

        {!loading && !error && mechanics.length > 0 && (
          <div className="admin-orders-table-wrap">
            <table className="admin-orders-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Tên hiển thị</th>
                  <th>Trạng thái</th>
                  <th>Lần đăng nhập cuối</th>
                  <th>Ngày tạo</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {mechanics.map((mechanic) => (
                  <tr key={mechanic.id}>
                    <td>{mechanic.id}</td>
                    <td><code>{mechanic.username}</code></td>
                    <td>{mechanic.display_name || "-"}</td>
                    <td>{mechanic.is_active ? "Đang dùng" : "Ngưng dùng"}</td>
                    <td>{mechanic.last_login_at ? new Date(mechanic.last_login_at).toLocaleString() : "-"}</td>
                    <td>{mechanic.created_at ? new Date(mechanic.created_at).toLocaleString() : "-"}</td>
                    <td>
                      <button
                        type="button"
                        className="admin-edit-button"
                        onClick={() => openResetPasswordModal(mechanic)}
                        disabled={resettingId === mechanic.id || togglingId === mechanic.id}
                      >
                        {resettingId === mechanic.id ? "Đang reset..." : "Reset mật khẩu"}
                      </button>
                      <button
                        type="button"
                        className={mechanic.is_active ? "admin-delete-button" : "admin-edit-button"}
                        onClick={() => handleToggleStatus(mechanic)}
                        disabled={togglingId === mechanic.id || resettingId === mechanic.id}
                      >
                        {togglingId === mechanic.id
                          ? "Đang cập nhật..."
                          : mechanic.is_active
                            ? "Disable"
                            : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {resetPasswordModal && (
          <div className="admin-modal-overlay" onClick={closeResetPasswordModal}>
            <div className="admin-modal admin-reset-password-modal" onClick={(event) => event.stopPropagation()}>
              <h2>Reset mật khẩu: <code>{resetPasswordModal.username}</code></h2>
              <p className="admin-orders-status">
                Nhập mật khẩu mới cho tài khoản người dùng {resetPasswordModal.display_name || resetPasswordModal.username}.
              </p>
              <label className="admin-modal-label">
                Mật khẩu mới
                <input
                  type="password"
                  className="admin-modal-input"
                  value={resetPasswordForm.password}
                  onChange={(event) => setResetPasswordForm((previous) => ({ ...previous, password: event.target.value }))}
                  autoComplete="new-password"
                />
              </label>
              <label className="admin-modal-label">
                Nhập lại mật khẩu mới
                <input
                  type="password"
                  className="admin-modal-input"
                  value={resetPasswordForm.confirmPassword}
                  onChange={(event) => setResetPasswordForm((previous) => ({ ...previous, confirmPassword: event.target.value }))}
                  autoComplete="new-password"
                />
              </label>
              {resetPasswordError && <p className="admin-orders-error">{resetPasswordError}</p>}
              <div className="admin-modal-actions">
                <button type="button" className="btn-primary" onClick={handleResetPassword} disabled={resettingId === resetPasswordModal.id}>
                  {resettingId === resetPasswordModal.id ? "Đang reset..." : "Lưu mật khẩu mới"}
                </button>
                <button type="button" className="admin-logout-button" onClick={closeResetPasswordModal} disabled={resettingId === resetPasswordModal.id}>Hủy</button>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

function SiteFooter() {
  const [totalVisits, setTotalVisits] = useState(null);

  useEffect(() => {
    let ignore = false;

    const getVisitToken = () => {
      if (typeof window === "undefined") {
        return null;
      }

      const existingToken = window.localStorage.getItem(SITE_VISIT_TOKEN_STORAGE_KEY);
      if (existingToken) {
        return existingToken;
      }

      const nextToken =
        typeof window.crypto?.randomUUID === "function"
          ? window.crypto.randomUUID()
          : `visit-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

      window.localStorage.setItem(SITE_VISIT_TOKEN_STORAGE_KEY, nextToken);
      return nextToken;
    };

    const loadVisitTotal = async () => {
      try {
        const visitToken = getVisitToken();
        const visitResponse = await fetch("/api/metrics/visit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            visitToken,
            path: window.location.pathname,
          }),
        });

        if (visitResponse.ok) {
          const visitData = await visitResponse.json();
          if (!ignore && Number.isFinite(visitData.totalVisits)) {
            setTotalVisits(visitData.totalVisits);
          }
          return;
        }

        const totalResponse = await fetch("/api/metrics/visit-total");
        if (!totalResponse.ok) {
          return;
        }

        const totalData = await totalResponse.json();
        if (!ignore && Number.isFinite(totalData.totalVisits)) {
          setTotalVisits(totalData.totalVisits);
        }
      } catch (error) {
        console.error("Error loading visit total:", error);
      }
    };

    loadVisitTotal();

    return () => {
      ignore = true;
    };
  }, []);

  const formattedTotalVisits =
    totalVisits === null ? null : new Intl.NumberFormat("vi-VN").format(totalVisits);

  return (
    <footer className="site-footer">
      <div className="site-footer-inner">

        {/* Column 1: Brand & about */}
        <div className="sf-col sf-col-brand">
          <div className="sf-logo">
            <img src={footerLogo} alt="Phụ Tùng Ô Tô" className="sf-logo-image" />
          </div>
          <p className="sf-tagline">
            Phụ tùng chính hãng, giao hàng toàn quốc.
          </p>
          <div className="sf-trust-badges">
            <span className="sf-badge">Chính hãng</span>
            <span className="sf-badge">Hỗ trợ nhanh</span>
          </div>
        </div>

        {/* Column 2: Quick links */}
        <div className="sf-col">
          <h4 className="sf-col-title">Trang chính</h4>
          <ul className="sf-links">
            <li><Link to="/home">Trang chủ</Link></li>
            <li><Link to="/search">Tìm phụ tùng</Link></li>
            <li><Link to="/order">Đặt hàng</Link></li>
            <li><Link to="/about">Giới thiệu</Link></li>
          </ul>
        </div>

        {/* Column 3: Services */}
        <div className="sf-col">
          <h4 className="sf-col-title">Dịch vụ</h4>
          <ul className="sf-links">
            <li>Tra cứu phụ tùng theo mã phụ tùng</li>
            <li>Tư vấn chọn phụ tùng</li>
            <li>Giao hàng toàn quốc</li>
            <li>Bảo hành &amp; đổi trả</li>
          </ul>
        </div>

        {/* Column 4: Contact */}
        <div className="sf-col">
          <h4 className="sf-col-title">Liên hệ</h4>
          <ul className="sf-links sf-contact">
            <li>
              <span className="sf-icon">📞</span>
              <a href="tel:0388861168">0388 861 168</a>
            </li>
            <li>
              <span className="sf-icon">✉️</span>
              <a href="mailto:info@phutungoto.shop">info@phutungoto.shop</a>
            </li>
            <li>
              <span className="sf-icon">📍</span>
              TDP Phượng Nghĩa, Phường Chương Mỹ, Hà Nội, Việt Nam
            </li>
            <li>
              <span className="sf-icon">🕐</span>
              Thứ 2 – Thứ 7: 8:00 – 18:00
            </li>
          </ul>
        </div>

      </div>

      {/* Bottom bar */}
      <div className="sf-bottom-bar">
        <span>© {new Date().getFullYear()} Phụ Tùng Ô Tô. Bảo lưu mọi quyền.</span>
        <span className="sf-visit-total" aria-live="polite">
          Đã phục vụ {formattedTotalVisits || "..."} lượt truy cập
        </span>
        <span className="sf-bottom-right">
          <span>Chính sách bảo mật</span>
          <span className="sf-divider">|</span>
          <span>Điều khoản sử dụng</span>
        </span>
      </div>
    </footer>
  );
}

function ContactChannelIcon({ icon }) {
  if (icon === "phone") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6.6 10.8a15.5 15.5 0 0 0 6.6 6.6l2.2-2.2a1.4 1.4 0 0 1 1.4-.3c1 .4 2 .6 3 .6.8 0 1.4.6 1.4 1.4V20a1.4 1.4 0 0 1-1.4 1.4A18.8 18.8 0 0 1 2.6 4.2 1.4 1.4 0 0 1 4 2.8h3.1c.8 0 1.4.6 1.4 1.4 0 1 .2 2 .6 3a1.4 1.4 0 0 1-.3 1.4l-2.2 2.2Z" fill="currentColor" />
      </svg>
    );
  }

  if (icon === "mail") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 6.8A1.8 1.8 0 0 1 4.8 5h14.4A1.8 1.8 0 0 1 21 6.8v10.4a1.8 1.8 0 0 1-1.8 1.8H4.8A1.8 1.8 0 0 1 3 17.2V6.8Zm1.8.1v.3L12 12l7.2-4.8v-.3H4.8Zm14.4 10.3V9.3l-6.8 4.5a.8.8 0 0 1-.9 0L4.8 9.3v7.9h14.4Z" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5.8A2.8 2.8 0 0 1 6.8 3h10.4A2.8 2.8 0 0 1 20 5.8v7.4A2.8 2.8 0 0 1 17.2 16H10l-4.6 4.1c-.5.5-1.4.1-1.4-.6V16A2.8 2.8 0 0 1 4 13.2V5.8Zm2.8-1.2c-.7 0-1.2.5-1.2 1.2v7.4c0 .7.5 1.2 1.2 1.2h3.8c.2 0 .4.1.5.2l3 2.7v-2.1c0-.4.4-.8.8-.8h4.3c.7 0 1.2-.5 1.2-1.2V5.8c0-.7-.5-1.2-1.2-1.2H6.8Z" fill="currentColor" />
    </svg>
  );
}

function FloatingContactButton() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  return (
    <div className={`floating-contact ${isOpen ? "open" : ""}`}>
      <div className="floating-contact-panel" aria-hidden={!isOpen}>
        <div className="floating-contact-panel-head">
          <strong>Liên hệ nhanh</strong>
          <span>Chọn kênh thuận tiện nhất để được tư vấn.</span>
        </div>

        <div className="floating-contact-actions">
          {CONTACT_CHANNELS.map((channel) => (
            <a
              key={channel.key}
              className="floating-contact-action"
              href={channel.href}
              target={channel.external ? "_blank" : undefined}
              rel={channel.external ? "noreferrer" : undefined}
            >
              <span className="floating-contact-action-icon">
                <ContactChannelIcon icon={channel.icon} />
              </span>
              <span className="floating-contact-action-text">
                <strong>{channel.label}</strong>
                <small>{channel.detail}</small>
              </span>
            </a>
          ))}

          <Link className="floating-contact-action floating-contact-action-order" to="/order">
            <span className="floating-contact-action-icon floating-contact-action-badge">+</span>
            <span className="floating-contact-action-text">
              <strong>Gửi yêu cầu</strong>
              <small>Đi tới form đặt hàng</small>
            </span>
          </Link>
        </div>
      </div>

      <button
        type="button"
        className="floating-contact-trigger"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        aria-controls="floating-contact-panel"
        aria-label={isOpen ? "Đóng liên hệ nhanh" : "Mở liên hệ nhanh"}
      >
        <span className="floating-contact-trigger-icon">
          {isOpen ? "×" : <ContactChannelIcon icon="chat" />}
        </span>
        <span className="floating-contact-trigger-text">Liên hệ</span>
      </button>

      <span className="floating-contact-pulse" aria-hidden="true" />
    </div>
  );
}

function App() {
  return (
    <Router>
      <div className="min-h-screen w-full bg-white text-slate-900">
        <header className="App-header">
          <div className="App-header-inner">
            <Link to="/home" className="App-brand" aria-label="Về trang chủ">
              <img src={headerLogo} alt="Phụ Tùng Ô Tô" className="App-brand-logo" />
            </Link>

            <nav className="App-nav">
              <NavLink
                to="/home"
                className={({ isActive }) =>
                  isActive ? "App-link active" : "App-link"
                }
              >
                Trang chủ
              </NavLink>
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  isActive ? "App-link active" : "App-link"
                }
              >
                Giới thiệu
              </NavLink>
              <NavLink
                to="/order"
                className={({ isActive }) =>
                  isActive ? "App-link active" : "App-link"
                }
              >
                Đặt hàng
              </NavLink>
            </nav>
          </div>
        </header>

        {/* Main content stays below the brand section, no overlap. */}
        <main className="App-main">
            <Routes>
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/home" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/order" element={<Order />} />
              <Route path="/search" element={<SearchParts />} />
              <Route path="/vins" element={<Navigate to="/home" replace />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/mechanic/login" element={<MechanicLogin />} />
              <Route path="/mechanic/uploads" element={<MechanicUploads />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/mechanics" element={<AdminMechanics />} />
              <Route path="/admin/posts" element={<AdminPosts />} />
              <Route path="/admin/mechanic-media" element={<AdminMechanicMedia />} />
              <Route path="/posts/:partNo" element={<PostDetail />} />
              <Route path="/posts/:partNo/:carSlug" element={<PostDetail />} />
            </Routes>
        </main>
        <SiteFooter />
        <FloatingContactButton />
      </div>
    </Router>
  );
}

export default App;
